import { useState, useEffect, useRef } from "react";
import TopBar from "../components/TopBar";
import Avatar from "../components/Avatar";
import Badge from "../components/Badge";
import FieldRow from "../components/FieldRow";
import Toast from "../components/Toast";
import { uid } from "../constants";
import { apiFetch } from "../utils/api";
import { loadWatermarkDataUrl, applyPdfWatermark } from "../utils/pdfWatermark";
import { todayIST, currentMonthIST } from "../utils/dateUtils";

const G2 = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>;
const G3 = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>{children}</div>;

// "" / undefined / null  →  SQL NULL.  Anything else is returned untouched.
const nul = v => (v === "" || v === null || v === undefined) ? null : v;

// Column length limits — mirror the DB schema (employees table).
// Keep this in sync with utils/excel.js LENGTHS and the DB column widths.
const FIELD_MAX = {
  code: 20,
  first_name: 60, last_name: 60,
  designation: 100,            // role
  department: 100,
  official_email: 180, personal_email: 180, ec_email: 180,
  primary_phone: 20, secondary_phone: 20, ec_phone: 20,
  blood_group: 10,
  nationality: 60,
  aadhaar_number: 12,
  pan_number: 10,
  passport_number: 20, passport_country: 100,
  addr_line: 200, addr_city: 100, addr_state: 100, addr_pin: 10,
  ec_first_name: 60, ec_last_name: 60, ec_relation: 60,
  reporting_manager: 120,
};
// ctc is DECIMAL(10,2) — 8 digits before decimal, 2 after → max 99,999,999.99
const CTC_MAX = 99999999.99;

// ── Shared metadata for all Excel exports ─────────────────────────────────────
// Source of truth: must match controllers/employeeController.js → generateTemplate.
// Used by:
//   • exportXLS              — table export (all filtered rows)
//   • view-modal Export Excel — single-employee export
//   • bulk-import template   — same columns, styled the same way (server-side)
const TEMPLATE_HEADERS = [
  "employee_id", "first_name", "last_name", "official_email",
  "designation", "department", "joining_date",
  "primary_phone", "secondary_phone", "personal_email",
  "gender", "dob", "blood_group", "nationality",
  "aadhaar_number", "pan_number",
  "curr_addr_line1", "curr_addr_line2", "curr_addr_city", "curr_addr_state", "curr_addr_pincode",
  "perm_addr_line1", "perm_addr_line2", "perm_addr_city", "perm_addr_state", "perm_addr_pincode",
  "ec_first_name", "ec_last_name", "ec_relation", "ec_phone", "ec_email",
  "reporting_manager", "base_ctc_inr",
  "passport_number", "passport_expiry", "passport_country",
  "employment_status",
];
const TEMPLATE_REQUIRED = new Set([
  "employee_id", "first_name", "last_name", "official_email",
  "designation", "department", "joining_date",
]);
const TEMPLATE_COL_WIDTHS = {
  employee_id: 16, first_name: 14, last_name: 14, official_email: 30,
  designation: 28, department: 20, joining_date: 15,
  primary_phone: 16, secondary_phone: 16, personal_email: 28,
  gender: 14, dob: 14, blood_group: 13, nationality: 14,
  aadhaar_number: 18, pan_number: 15,
  curr_addr_line1: 24, curr_addr_line2: 18, curr_addr_city: 15, curr_addr_state: 15, curr_addr_pincode: 15,
  perm_addr_line1: 24, perm_addr_line2: 18, perm_addr_city: 15, perm_addr_state: 15, perm_addr_pincode: 15,
  ec_first_name: 16, ec_last_name: 16, ec_relation: 14, ec_phone: 16, ec_email: 26,
  reporting_manager: 18, base_ctc_inr: 14,
  passport_number: 16, passport_expiry: 16, passport_country: 16,
  employment_status: 20,
};

const onlyDate = v => (v ? String(v).slice(0, 10) : "");

// Maps a normalised employee object → array of cell values matching TEMPLATE_HEADERS.
const empToTemplateRow = e => [
  e.code || "",
  e.firstName || (e.name?.split(" ")[0]) || "",
  e.lastName || (e.name?.split(" ").slice(1).join(" ")) || "",
  e.officialEmail || "",
  e.designation || "",
  e.department || "",
  onlyDate(e.joinDate),
  e.primaryPhone || "",
  e.secondaryPhone || "",
  e.personalEmail || "",
  e.gender || "",
  onlyDate(e.dob),
  e.bloodGroup || "",
  e.nationality || "",
  e.aadhaar || "",
  e.pan || "",
  (e.currentAddr || {}).line1 || "",
  (e.currentAddr || {}).line2 || "",
  (e.currentAddr || {}).city || "",
  (e.currentAddr || {}).state || "",
  (e.currentAddr || {}).pin || "",
  (e.permanentAddr || {}).line1 || "",
  (e.permanentAddr || {}).line2 || "",
  (e.permanentAddr || {}).city || "",
  (e.permanentAddr || {}).state || "",
  (e.permanentAddr || {}).pin || "",
  (e.emergency || {}).firstName || "",
  (e.emergency || {}).lastName || "",
  (e.emergency || {}).relation || "",
  (e.emergency || {}).phone || "",
  (e.emergency || {}).email || "",
  e.reportingManager || "",
  e.ctc !== undefined && e.ctc !== null && e.ctc !== "" ? Number(e.ctc) : "",
  (e.passport || {}).no || "",
  onlyDate((e.passport || {}).expiry),
  (e.passport || {}).country || "",
  e.status || "",
];

// ── Dropdown reference values ────────────────────────────────────────────────
// Source of truth: controllers/employeeController.js → generateTemplate.
// Used by attachEmployeeDropdownsAndValidations() below so the table-export and
// single-employee-export files carry the same in-cell dropdowns as the import
// template — without them, a user editing the exported file could type free-form
// "Senior Tech Lead" into designation, save, and re-import garbage.
const DV_DEPARTMENTS = [
  "Compliance / Legal / Security / InfoSec",
  "Database Administration (DBA)",
  "Executive Management",
  "Finance & Accounts Payable",
  "Healthcare Operations",
  "HR / Talent Acquisition / People Officer",
  "IT Operations / System Administration",
  "Operations Management",
  "Procurement / Administration",
  "Project / Product Management",
  "Sales & Marketing",
  "Software Development / DevOps",
  "Support / IT Helpdesk",
  "Testing / Quality Assurance",
  "Training & Learning Development",
];
const DV_DESIGNATIONS = [
  "Account Manager", "Administrative Officer", "Assistant Manager", "Associate",
  "Associate Consultant", "Back-End Developer", "Billing Coordinator",
  "Business Development Executive", "Business Development Manager",
  "Client Relationship Manager", "Compliance Officer", "Consultant",
  "Database Administrator (DBA)", "Delivery Manager", "Front-End Developer",
  "Full Stack Developer", "Graphic / UI/UX Designer", "HR Manager",
  "International Coding Specialist", "IT Support Engineer", "Junior Developer",
  "Manager", "Medical Biller", "Medical Coder", "Process Associate",
  "Procurement / Support Staff", "Project Coordinator", "Project Manager",
  "Quality Assurance Auditor", "Reimbursement Coordinator", "Reimbursement Manager",
  "Senior Manager", "Senior Quality Assurance Auditor", "Software Engineer",
  "System Administrator", "Managing Director", "CEO", "CFO", "COO", "CCRO",
  "CPO", "CHRO", "Director", "CxO", "Portfolio Delivery Manager",
];
const DV_BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-", "Unknown"];
const DV_GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say", "Other"];
const DV_STATUSES = ["Active", "On Leave", "Probation", "Notice Period", "Inactive"];

// 1-based column index in TEMPLATE_HEADERS → Dropdown_Values column letter & list.
// (keep aligned with employeeController.js generateTemplate DROPDOWN_COLS)
const TEMPLATE_DROPDOWN_COLS = {
  5:  { col: "B", values: DV_DESIGNATIONS }, // designation
  6:  { col: "A", values: DV_DEPARTMENTS },  // department
  11: { col: "D", values: DV_GENDERS },      // gender
  13: { col: "C", values: DV_BLOOD_GROUPS }, // blood_group
  37: { col: "E", values: DV_STATUSES },     // employment_status
};

// Adds a "Dropdown_Values" sheet to the workbook and applies in-cell validation
// to the dropdown columns of `ws`. Call AFTER writeTemplateStyledSheet so the
// data sheet exists and we know how many rows it has.
const attachEmployeeDropdownsAndValidations = (wb, ws, dataRowCount) => {
  const colLetter = n =>
    n <= 26
      ? String.fromCharCode(64 + n)
      : String.fromCharCode(64 + Math.floor((n - 1) / 26)) + String.fromCharCode(65 + ((n - 1) % 26));

  const validRowEnd = Math.max((dataRowCount || 0) + 1, 500);
  Object.entries(TEMPLATE_DROPDOWN_COLS).forEach(([colIdx, { col, values }]) => {
    const cl = colLetter(parseInt(colIdx));
    const formula = `'Dropdown_Values'!$${col}$2:$${col}$${values.length + 1}`;
    for (let row = 2; row <= validRowEnd; row++) {
      ws.getCell(`${cl}${row}`).dataValidation = {
        type: "list", allowBlank: true, formulae: [formula],
        showDropDown: false, showErrorMessage: true,
        errorTitle: "Invalid value",
        error: "Please select from the dropdown, or leave blank if optional.",
      };
    }
  });

  const wsDV = wb.addWorksheet("Dropdown_Values");
  wsDV.columns = [{ width: 28 }, { width: 28 }, { width: 14 }, { width: 18 }, { width: 18 }];
  const dvCols = [
    { title: "Departments", values: DV_DEPARTMENTS, col: 1 },
    { title: "Designations", values: DV_DESIGNATIONS, col: 2 },
    { title: "Blood Groups", values: DV_BLOOD_GROUPS, col: 3 },
    { title: "Genders", values: DV_GENDERS, col: 4 },
    { title: "Employment Status", values: DV_STATUSES, col: 5 },
  ];
  dvCols.forEach(({ title, col }) => {
    const cell = wsDV.getCell(1, col);
    cell.value = title;
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E3A5F" } };
    cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10 };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = { top: { style: "thin", color: { argb: "FFD1D5DB" } }, left: { style: "thin", color: { argb: "FFD1D5DB" } }, bottom: { style: "thin", color: { argb: "FFD1D5DB" } }, right: { style: "thin", color: { argb: "FFD1D5DB" } } };
  });
  wsDV.getRow(1).height = 26;
  const maxRows = Math.max(...dvCols.map(c => c.values.length));
  for (let r = 0; r < maxRows; r++) {
    dvCols.forEach(({ values, col }) => {
      const val = values[r] ?? null;
      if (val === null) return;
      const cell = wsDV.getCell(r + 2, col);
      cell.value = val;
      cell.font = { size: 9, color: { argb: "FF374151" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: r % 2 === 0 ? "FFFFFFFF" : "FFF9FAFB" } };
      cell.alignment = { vertical: "middle" };
      cell.border = { top: { style: "thin", color: { argb: "FFE5E7EB" } }, left: { style: "thin", color: { argb: "FFE5E7EB" } }, bottom: { style: "thin", color: { argb: "FFE5E7EB" } }, right: { style: "thin", color: { argb: "FFE5E7EB" } } };
    });
  }
};

// Styles `ws` so it matches the bulk-import template look:
// orange header for required, dark-grey for optional, zebra rows, borders.
const writeTemplateStyledSheet = (ws, dataRows) => {
  ws.columns = TEMPLATE_HEADERS.map(h => ({ key: h, width: TEMPLATE_COL_WIDTHS[h] || 18 }));
  ws.views = [{ state: "frozen", ySplit: 1 }];

  // Header row
  const hRow = ws.getRow(1);
  hRow.height = 34;
  TEMPLATE_HEADERS.forEach((h, i) => {
    const cell = hRow.getCell(i + 1);
    cell.value = h;
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: TEMPLATE_REQUIRED.has(h) ? "FFE8520A" : "FF374151" } };
    cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 9 };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = {
      top: { style: "thin", color: { argb: "FFD1D5DB" } },
      left: { style: "thin", color: { argb: "FFD1D5DB" } },
      bottom: { style: "thin", color: { argb: "FFD1D5DB" } },
      right: { style: "thin", color: { argb: "FFD1D5DB" } },
    };
  });

  // Data rows — zebra striping
  dataRows.forEach((row, idx) => {
    const dataRow = ws.getRow(idx + 2);
    dataRow.height = 18;
    const rowBg = idx % 2 === 0 ? "FFFFFFFF" : "FFFAFAFA";
    row.forEach((val, ci) => {
      const cell = dataRow.getCell(ci + 1);
      cell.value = val;
      cell.font = { size: 9, color: { argb: "FF374151" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: rowBg } };
      cell.alignment = { vertical: "middle" };
      cell.border = {
        top: { style: "thin", color: { argb: "FFE5E7EB" } },
        left: { style: "thin", color: { argb: "FFE5E7EB" } },
        bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
        right: { style: "thin", color: { argb: "FFE5E7EB" } },
      };
    });
  });
};

// Custom searchable dropdown — keeps the option list inside the parent, with its
// own scroll and a filter input. Used for long option lists where the browser's
// native popup would extend past the modal.
const Combo = ({ value, onChange, options, placeholder = "Select", error = false }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [dropUp, setDropUp] = useState(false);
  const btnRef = useRef(null);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) { setQ(""); return; }
    const onClose = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClose);
    return () => document.removeEventListener("mousedown", onClose);
  }, [open]);

  const filtered = q.trim()
    ? options.filter(o => o.toLowerCase().includes(q.trim().toLowerCase()))
    : options;

  const handleToggle = () => {
    if (!open && btnRef.current) {
      // Decide whether to open upward or downward based on space below
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropUp(spaceBelow < 260);
    }
    setOpen(o => !o);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button ref={btnRef} type="button" className="inp" onClick={handleToggle}
        style={{
          width: "100%", textAlign: "left", background: "#fff", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderColor: error ? "#EF4444" : "",
        }}>
        <span style={{ color: value ? "#0D1B2A" : "#94A3B8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value || placeholder}</span>
        <span style={{ fontSize: 10, color: "#94A3B8", marginLeft: 8, flexShrink: 0 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{
          position: "absolute",
          ...(dropUp
            ? { bottom: "calc(100% + 4px)" }
            : { top: "calc(100% + 4px)" }),
          left: 0,
          width: "100%",
          background: "#fff",
          border: "1px solid #E2E8F0",
          borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,.12)",
          maxHeight: 240,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}>
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search…"
            style={{
              padding: "9px 12px", fontSize: 12, border: "none",
              borderBottom: "1px solid #F1F5F9", outline: "none", flexShrink: 0,
            }}
          />
          <div style={{ overflowY: "auto", flex: 1 }}>
            {filtered.length === 0 && (
              <div style={{ padding: "12px 14px", fontSize: 12, color: "#94A3B8", textAlign: "center" }}>No matches</div>
            )}
            {filtered.map(opt => {
              const selected = value === opt;
              return (
                <div key={opt} onClick={() => { onChange(opt); setOpen(false); }}
                  style={{
                    padding: "9px 14px", fontSize: 12, cursor: "pointer",
                    background: selected ? "#FFF5F0" : "transparent",
                    color: selected ? "#E8520A" : "#0D1B2A",
                    fontWeight: selected ? 600 : 400,
                    borderBottom: "1px solid #F8FAFC",
                  }}
                  onMouseEnter={e => { if (!selected) e.currentTarget.style.background = "#F8FAFC"; }}
                  onMouseLeave={e => { if (!selected) e.currentTarget.style.background = "transparent"; }}>
                  {opt}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const AddEmployeeModal = ({ onSave, onClose, existing, empList = [] }) => {
  const isEdit = !!existing;
  const blank = {
    code: "", firstName: "", lastName: "", name: "", dob: "", gender: "", bloodGroup: "", nationality: "Indian",
    aadhaar: "", pan: "", passport: { no: "", expiry: "", country: "" },
    primaryPhone: "", secondaryPhone: "", officialEmail: "", personalEmail: "",
    currentAddr: { line1: "", line2: "", city: "", state: "", pin: "" },
    permanentAddr: { line1: "", line2: "", city: "", state: "", pin: "" },
    emergency: { firstName: "", lastName: "", name: "", relation: "", phone: "", email: "" },
    designation: "", department: "", joinDate: "", status: "Active", reportingManager: "",
    education: [{ degree: "", institution: "", year: "", grade: "", specialization: "" }],
    workHistory: [],
    skills: [], skillInput: "",
    resumeFile: "", resumeFileObj: null, notes: "", ctc: ""
  };
  const [tab, setTab] = useState(0);
  const [f, setF] = useState(isEdit ? { ...blank, ...existing, firstName: existing.firstName || existing.name?.split(" ")[0] || "", lastName: existing.lastName || existing.name?.split(" ").slice(1).join(" ") || "", passport: { ...blank.passport, ...(existing.passport || {}) }, emergency: { ...blank.emergency, ...(existing.emergency || {}), firstName: (existing.emergency?.firstName) || (existing.emergency?.name?.split(" ")[0]) || "", lastName: (existing.emergency?.lastName) || (existing.emergency?.name?.split(" ").slice(1).join(" ")) || "" }, skills: existing.skills || [], skillInput: "", resumeFileObj: null } : blank);
  const [touched, setTouched] = useState(false);
  const [sameAddr, setSameAddr] = useState(isEdit ? Boolean(existing.perm_addr_same_as_curr) : false);
  const [fileError, setFileError] = useState("");
  const resumeInputRef = useRef(null);
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  const updNested = (obj, key, v) => setF(p => ({ ...p, [obj]: { ...p[obj], [key]: v } }));

  const TABS = ["Personal & ID", "Contact & Address", "Professional", "Education & History", "Notes & Resume", "Review & Create"];
  const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-", "Unknown"];
  const DEPARTMENTS = [
    "Compliance / Legal / Security / InfoSec",
    "Database Administration (DBA)",
    "Executive Management",
    "Finance & Accounts Payable",
    "Healthcare Operations",
    "HR / Talent Acquisition / People Officer",
    "IT Operations / System Administration",
    "Operations Management",
    "Procurement / Administration",
    "Project / Product Management",
    "Sales & Marketing",
    "Software Development / DevOps",
    "Support / IT Helpdesk",
    "Testing / Quality Assurance",
    "Training & Learning Development",
  ];
  const DESIGNATIONS = [
    "Account Manager",
    "Administrative Officer",
    "Assistant Manager",
    "Associate",
    "Associate Consultant",
    "Back-End Developer",
    "Billing Coordinator",
    "Business Development Executive",
    "Business Development Manager",
    "Client Relationship Manager",
    "Compliance Officer",
    "Consultant",
    "Database Administrator (DBA)",
    "Delivery Manager",
    "Front-End Developer",
    "Full Stack Developer",
    "Graphic / UI/UX Designer",
    "HR Manager",
    "International Coding Specialist",
    "IT Support Engineer",
    "Junior Developer",
    "Manager",
    "Medical Biller",
    "Medical Coder",
    "Process Associate",
    "Procurement / Support Staff",
    "Project Coordinator",
    "Project Manager",
    "Quality Assurance Auditor",
    "Reimbursement Coordinator",
    "Reimbursement Manager",
    "Senior Manager",
    "Senior Quality Assurance Auditor",
    "Software Engineer",
    "System Administrator",
    "Managing Director",
    "CEO",
    "CFO",
    "COO",
    "CCRO",
    "CPO",
    "CHRO",
    "Director",
    "CxO",
    "Portfolio Delivery Manager",
  ];
  const STATUSES = ["Active", "On Leave", "Probation", "Notice Period", "Inactive"];

  // Education handlers
  const addEdu = () => setF(p => ({ ...p, education: [...p.education, { degree: "", institution: "", year: "", grade: "", specialization: "" }] }));
  const updEdu = (i, k, v) => setF(p => ({ ...p, education: p.education.map((e, j) => j === i ? { ...e, [k]: v } : e) }));
  const delEdu = i => setF(p => ({ ...p, education: p.education.filter((_, j) => j !== i) }));

  // Work history handlers
  const addWork = () => setF(p => ({ ...p, workHistory: [...p.workHistory, { company: "", role: "", from: "", to: "", reason: "", ctc: "" }] }));
  const updWork = (i, k, v) => setF(p => ({ ...p, workHistory: p.workHistory.map((w, j) => j === i ? { ...w, [k]: v } : w) }));
  const delWork = i => setF(p => ({ ...p, workHistory: p.workHistory.filter((_, j) => j !== i) }));

  // Skills
  const addSkill = () => { const s = f.skillInput.trim(); if (!s || f.skills.includes(s)) return; setF(p => ({ ...p, skills: [...p.skills, s], skillInput: "" })); };
  const delSkill = s => setF(p => ({ ...p, skills: p.skills.filter(x => x !== s) }));

  // Copy current to permanent
  const syncAddr = (v) => { setSameAddr(v); if (v) setF(p => ({ ...p, permanentAddr: { ...p.currentAddr } })); };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const validationErrors = [];

  // Required
  if (!f.firstName) validationErrors.push("First Name is required");
  else if (f.firstName.length > FIELD_MAX.first_name) validationErrors.push(`First Name must be ${FIELD_MAX.first_name} characters or less`);
  if (!f.lastName) validationErrors.push("Last Name is required");
  else if (f.lastName.length > FIELD_MAX.last_name) validationErrors.push(`Last Name must be ${FIELD_MAX.last_name} characters or less`);
  if (!f.code) validationErrors.push("Employee Id is required");
  else if (!/^DZIND\d+$/.test(f.code.trim().toUpperCase())) validationErrors.push("Employee Id must follow the format DZIND### (e.g. DZIND001) — the 'DZIND' prefix is required and the suffix must be digits only");
  else if (f.code.length > FIELD_MAX.code) validationErrors.push(`Employee Id must be ${FIELD_MAX.code} characters or less`);
  else if (!isEdit && empList.some(e => e.code?.toLowerCase() === f.code.toLowerCase())) validationErrors.push("Employee Id \"" + f.code + "\" already exists — use a different Id.");
  if (!f.officialEmail) validationErrors.push("Official Email is required");
  else if (!emailRegex.test(f.officialEmail)) validationErrors.push("Official Email format is invalid");
  else if (f.officialEmail.length > FIELD_MAX.official_email) validationErrors.push(`Official Email must be ${FIELD_MAX.official_email} characters or less`);
  if (!f.designation) validationErrors.push("Designation is required");
  if (!f.department) validationErrors.push("Department is required");
  if (!f.joinDate) validationErrors.push("DOJ is required");

  // Optional but format-checked when present
  if (f.personalEmail) {
    if (!emailRegex.test(f.personalEmail)) validationErrors.push("Personal Email format is invalid");
    else if (f.personalEmail.length > FIELD_MAX.personal_email) validationErrors.push(`Personal Email must be ${FIELD_MAX.personal_email} characters or less`);
  }
  if (f.primaryPhone) {
    if (f.primaryPhone.length < 7) validationErrors.push("Primary Phone must be at least 7 digits");
    else if (f.primaryPhone.length > FIELD_MAX.primary_phone) validationErrors.push(`Primary Phone must be ${FIELD_MAX.primary_phone} digits or less`);
  }
  if (f.secondaryPhone) {
    if (f.secondaryPhone.length < 7) validationErrors.push("Secondary Phone must be at least 7 digits");
    else if (f.secondaryPhone.length > FIELD_MAX.secondary_phone) validationErrors.push(`Secondary Phone must be ${FIELD_MAX.secondary_phone} digits or less`);
  }
  if (f.aadhaar && f.aadhaar.length !== 12) validationErrors.push("Aadhaar must be exactly 12 digits");
  if (f.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(f.pan)) validationErrors.push("PAN format invalid (e.g. ABCDE1234F)");
  if (f.nationality && f.nationality.length > FIELD_MAX.nationality) validationErrors.push(`Nationality must be ${FIELD_MAX.nationality} characters or less`);
  if (f.passport?.no && f.passport.no.length > FIELD_MAX.passport_number) validationErrors.push(`Passport No. must be ${FIELD_MAX.passport_number} characters or less`);
  if (f.passport?.country && f.passport.country.length > FIELD_MAX.passport_country) validationErrors.push(`Passport Country must be ${FIELD_MAX.passport_country} characters or less`);
  if (f.currentAddr?.pin && f.currentAddr.pin.length !== 6) validationErrors.push("Current address PIN must be 6 digits");
  if (f.permanentAddr?.pin && f.permanentAddr.pin.length !== 6) validationErrors.push("Permanent address PIN must be 6 digits");

  // Emergency contact
  if (f.emergency?.phone) {
    if (f.emergency.phone.length < 7) validationErrors.push("Emergency Phone must be at least 7 digits");
    else if (f.emergency.phone.length > FIELD_MAX.ec_phone) validationErrors.push(`Emergency Phone must be ${FIELD_MAX.ec_phone} digits or less`);
  }
  if (f.emergency?.email && !emailRegex.test(f.emergency.email)) validationErrors.push("Emergency Email format is invalid");

  // CTC — DECIMAL(10,2): 0 to 99,999,999.99
  if (f.ctc !== "" && f.ctc !== undefined && f.ctc !== null) {
    const ctcNum = Number(f.ctc);
    if (!isFinite(ctcNum) || ctcNum < 0) validationErrors.push("CTC must be a non-negative number");
    else if (ctcNum > CTC_MAX) validationErrors.push(`CTC must be ${CTC_MAX.toLocaleString("en-IN")} or less (max 8 digits + 2 decimals)`);
  }

  // Reporting manager
  if (f.reportingManager && f.reportingManager.length > FIELD_MAX.reporting_manager) validationErrors.push(`Reporting Manager must be ${FIELD_MAX.reporting_manager} characters or less`);

  // #6 — Work history dates: To >= From, both in the past (or current month).
  const todayMonth = currentMonthIST(); // "YYYY-MM" in IST (was UTC, off-by-one in Jan India midnight)
  (f.workHistory || []).forEach((w, i) => {
    if (w.from && w.from > todayMonth) validationErrors.push(`Work history #${i + 1}: "From" date cannot be in the future`);
    if (w.to && w.to > todayMonth) validationErrors.push(`Work history #${i + 1}: "To" date cannot be in the future`);
    if (w.from && w.to && w.to < w.from) validationErrors.push(`Work history #${i + 1}: "To" date must be after "From"`);
  });

  const required = validationErrors.length === 0;

  const submit = () => {
    setTouched(true);
    if (!required) return; // errors shown in summary tab
    const emp = { ...f, id: isEdit ? f.id : "E" + uid(), name: [f.firstName, f.lastName].filter(Boolean).join(" "), skillInput: undefined, sameAddr };
    emp.emergency = { ...f.emergency, name: [f.emergency.firstName, f.emergency.lastName].filter(Boolean).join(" ") || f.emergency.name };
    delete emp.skillInput;
    onSave(emp);
  };

  const inp = (v, onChange, ph, type = "text", req = false, maxLen) => (
    <input className="inp" type={type} value={v}
      onChange={e => onChange(maxLen ? e.target.value.slice(0, maxLen) : e.target.value)}
      placeholder={ph} maxLength={maxLen || undefined}
      style={{ borderColor: touched && req && !v ? "#EF4444" : "" }}
      onClick={(type === "date" || type === "month") ? (e => e.target.showPicker?.()) : undefined} />
  );

  // Digits-only input (phone numbers, PIN, Aadhaar, year)
  const digitsOnly = (v, onChange, ph, maxLen, req = false) => (
    <input className="inp" value={v}
      onChange={e => { const val = e.target.value.replace(/\D/g, ""); onChange(maxLen ? val.slice(0, maxLen) : val); }}
      onKeyDown={e => {
        if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(e.key)) return;
        if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
        if (!/^[0-9]$/.test(e.key)) e.preventDefault();
      }}
      placeholder={ph} inputMode="numeric" maxLength={maxLen || undefined}
      style={{ borderColor: touched && req && !v ? "#EF4444" : "" }} />
  );

  // Letters + spaces + hyphens only (names, relationships)
  const alphaOnly = (v, onChange, ph, req = false, maxLen) => (
    <input className="inp" value={v}
      onChange={e => {
        const val = e.target.value.replace(/[^a-zA-Z\s\-'.]/g, "");
        onChange(maxLen ? val.slice(0, maxLen) : val);
      }}
      onKeyDown={e => {
        if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End", "Space", " "].includes(e.key)) return;
        if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
        if (!/^[a-zA-Z\s\-'.]$/.test(e.key)) e.preventDefault();
      }}
      placeholder={ph} maxLength={maxLen || undefined}
      style={{ borderColor: touched && req && !v ? "#EF4444" : "" }} />
  );

  // Alphanumeric + dash/underscore (employee code, passport number)
  const alphaNum = (v, onChange, ph, upper = false, maxLen, req = false) => (
    <input className="inp" value={v}
      onChange={e => {
        let val = e.target.value.replace(/[^a-zA-Z0-9\-_]/g, "");
        if (upper) val = val.toUpperCase();
        onChange(maxLen ? val.slice(0, maxLen) : val);
      }}
      onKeyDown={e => {
        if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
        if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
        if (!/^[a-zA-Z0-9\-_]$/.test(e.key)) e.preventDefault();
      }}
      placeholder={ph} maxLength={maxLen || undefined}
      style={{ borderColor: touched && req && !v ? "#EF4444" : "" }} />
  );

  // PAN: 5 uppercase letters + 4 digits + 1 uppercase letter
  const panInput = (v, onChange) => (
    <input className="inp" value={v}
      onChange={e => { const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 10); onChange(val); }}
      onKeyDown={e => {
        if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
        if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
        if (!/^[a-zA-Z0-9]$/.test(e.key)) e.preventDefault();
      }}
      placeholder="ABCDE1234F" maxLength={10}
      style={{
        fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1,
        borderColor: v && v.length === 10 && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v) ? "#EF4444" : ""
      }} />
  );

  // Non-negative number input (CTC, work history CTC) with optional upper-bound
  const posNumInp = (v, onChange, ph, allowDecimal = true, max) => (
    <input className="inp" type="number" value={v} min="0" max={max}
      onChange={e => {
        const raw = e.target.value;
        if (raw === "") return onChange("");
        const n = Number(raw);
        if (!isFinite(n) || n < 0) return;
        if (max !== undefined && n > max) return;   // clamp
        onChange(raw);
      }}
      onKeyDown={e => {
        const blocked = ["-", "+", "e", "E"];
        if (!allowDecimal) blocked.push(".");
        if (blocked.includes(e.key)) e.preventDefault();
      }}
      placeholder={ph} />
  );
  const lbl = (text, req) => (
    <label style={{ fontSize: 11, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.3 }}>
      {text}{req && <span style={{ color: "#EF4444", marginLeft: 2 }}>*</span>}
    </label>
  );
  const frow = (label, children, req) => (
    <div style={{ marginBottom: 14 }}>{lbl(label, req)}{children}</div>
  );

  const tabContent = [
    // Tab 0 — Personal & ID
    <div key={0}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#E8520A", marginBottom: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>Personal Information</div>
      <G2>
        {frow("First Name", alphaOnly(f.firstName, v => upd("firstName", v), "First Name", true, FIELD_MAX.first_name), true)}
        {frow("Last Name", alphaOnly(f.lastName, v => upd("lastName", v), "Last Name", true, FIELD_MAX.last_name), true)}
        {frow("Employee Id", isEdit
          ? (
            <>
              <input className="inp" value={f.code} readOnly disabled
                style={{ background: "#F8FAFC", color: "#475569", cursor: "not-allowed", fontFamily: "'JetBrains Mono',monospace" }} />
              <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 4 }}>
                Employee Id cannot be updated &mdash; it is the primary key referenced by allocations, reviews, and email history.
              </div>
            </>
          )
          : (
            <>
              {alphaNum(f.code, v => upd("code", v), "DZIND###", true, FIELD_MAX.code, true)}
              <div style={{
                marginTop: 6, padding: "6px 10px", background: "#FFFBEB",
                border: "1px solid #FDE68A", borderRadius: 6, fontSize: 10, color: "#92400E",
                display: "flex", alignItems: "center", gap: 6
              }}>
                <span>⚠</span>
                <span>Employee Id is permanent — it cannot be updated after the employee is created.</span>
              </div>
            </>
          ), true)}
      </G2>
      <G3>
        {frow("Date of Birth", inp(f.dob, v => upd("dob", v), "", "date"))}
        {frow("Gender", <Combo
          value={f.gender}
          onChange={v => upd("gender", v)}
          options={["Male", "Female", "Non-binary", "Prefer not to say", "Other"]}
          placeholder="Select gender"
        />)}
        {frow("Blood Group", <Combo
          value={f.bloodGroup}
          onChange={v => upd("bloodGroup", v)}
          options={BLOOD_GROUPS}
          placeholder="Select blood group"
        />)}
      </G3>
      {frow("Nationality", inp(f.nationality, v => upd("nationality", v), "Indian", "text", false, FIELD_MAX.nationality))}
      <div style={{ borderTop: "1px solid #F1F5F9", margin: "16px 0" }} />
      <div style={{ fontSize: 12, fontWeight: 700, color: "#E8520A", marginBottom: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>Identity Documents</div>
      <G2>
        {frow("Aadhaar Number", digitsOnly(f.aadhaar, v => upd("aadhaar", v), "XXXX XXXX XXXX", 12))}
        {frow("PAN Number", panInput(f.pan, v => upd("pan", v)))}
      </G2>
      <G3>
        {frow("Passport No.", alphaNum(f.passport.no, v => updNested("passport", "no", v), "P#######", true, FIELD_MAX.passport_number))}
        {frow("Passport Expiry", inp(f.passport.expiry, v => updNested("passport", "expiry", v), "", "date"))}
        {frow("Issuing Country", inp(f.passport.country, v => updNested("passport", "country", v), "India", "text", false, FIELD_MAX.passport_country))}
      </G3>
    </div>,

    // Tab 1 — Contact & Address
    <div key={1}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#E8520A", marginBottom: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>Contact Details</div>
      <G2>
        {frow("Primary Phone", digitsOnly(f.primaryPhone, v => upd("primaryPhone", v), "10-digit mobile", FIELD_MAX.primary_phone))}
        {frow("Secondary Phone", digitsOnly(f.secondaryPhone, v => upd("secondaryPhone", v), "Alternate / landline", FIELD_MAX.secondary_phone))}
      </G2>
      <G2>
        {frow("Official Email", inp(f.officialEmail, v => upd("officialEmail", v), "name@dolluzcorp.com", "email", true, FIELD_MAX.official_email), true)}
        {frow("Personal Email", inp(f.personalEmail, v => upd("personalEmail", v), "name@gmail.com", "email", false, FIELD_MAX.personal_email))}
      </G2>
      <div style={{ borderTop: "1px solid #F1F5F9", margin: "16px 0" }} />
      <div style={{ fontSize: 12, fontWeight: 700, color: "#E8520A", marginBottom: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>Current Address</div>
      {frow("Street / Flat", inp(f.currentAddr.line1, v => updNested("currentAddr", "line1", v), "House/Flat, Street", "text", false, FIELD_MAX.addr_line))}
      {frow("Landmark / Area", inp(f.currentAddr.line2, v => updNested("currentAddr", "line2", v), "Area, Landmark (optional)", "text", false, FIELD_MAX.addr_line))}
      <G3>
        {frow("City", inp(f.currentAddr.city, v => updNested("currentAddr", "city", v), "City", "text", false, FIELD_MAX.addr_city))}
        {frow("State", inp(f.currentAddr.state, v => updNested("currentAddr", "state", v), "State", "text", false, FIELD_MAX.addr_state))}
        {frow("PIN Code", digitsOnly(f.currentAddr.pin, v => updNested("currentAddr", "pin", v), "6-digit PIN", 6))}
      </G3>
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0 14px" }}>
        <input type="checkbox" id="sameAddr" checked={sameAddr} onChange={e => syncAddr(e.target.checked)} style={{ width: 16, height: 16 }} />
        <label htmlFor="sameAddr" style={{ fontSize: 12, color: "#475569", cursor: "pointer" }}>Permanent address is same as current address</label>
      </div>
      {!sameAddr && <>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#E8520A", marginBottom: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>Permanent Address</div>
        {frow("Street / Flat", inp(f.permanentAddr.line1, v => updNested("permanentAddr", "line1", v), "House/Flat, Street", "text", false, FIELD_MAX.addr_line))}
        {frow("Landmark / Area", inp(f.permanentAddr.line2, v => updNested("permanentAddr", "line2", v), "Area, Landmark (optional)", "text", false, FIELD_MAX.addr_line))}
        <G3>
          {frow("City", inp(f.permanentAddr.city, v => updNested("permanentAddr", "city", v), "City", "text", false, FIELD_MAX.addr_city))}
          {frow("State", inp(f.permanentAddr.state, v => updNested("permanentAddr", "state", v), "State", "text", false, FIELD_MAX.addr_state))}
          {frow("PIN Code", digitsOnly(f.permanentAddr.pin, v => updNested("permanentAddr", "pin", v), "6-digit PIN", 6))}
        </G3>
      </>}
      <div style={{ borderTop: "1px solid #F1F5F9", margin: "16px 0" }} />
      <div style={{ fontSize: 12, fontWeight: 700, color: "#E8520A", marginBottom: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>Emergency Contact</div>
      <G2>
        {frow("First Name", inp(f.emergency.firstName, v => updNested("emergency", "firstName", v), "First Name", "text", false, FIELD_MAX.ec_first_name))}
        {frow("Last Name", inp(f.emergency.lastName, v => updNested("emergency", "lastName", v), "Last Name", "text", false, FIELD_MAX.ec_last_name))}
        {frow("Relationship", inp(f.emergency.relation, v => updNested("emergency", "relation", v), "Spouse, Parent, Sibling…", "text", false, FIELD_MAX.ec_relation))}
      </G2>
      <G2>
        {frow("Phone Number", digitsOnly(f.emergency.phone, v => updNested("emergency", "phone", v), "Mobile number", FIELD_MAX.ec_phone))}
        {frow("Email (optional)", inp(f.emergency.email, v => updNested("emergency", "email", v), "email@example.com", "email", false, FIELD_MAX.ec_email))}
      </G2>
    </div>,

    // Tab 2 — Professional
    <div key={2}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#E8520A", marginBottom: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>Employment Details</div>
      <G2>
        {frow("Designation", <Combo
          value={f.designation}
          onChange={v => upd("designation", v)}
          options={DESIGNATIONS}
          placeholder="Select designation"
          error={touched && !f.designation}
        />, true)}
        {frow("Department", <Combo
          value={f.department}
          onChange={v => upd("department", v)}
          options={DEPARTMENTS}
          placeholder="Select department"
          error={touched && !f.department}
        />, true)}
      </G2>
      <G3>
        {frow("DOJ", inp(f.joinDate, v => upd("joinDate", v), "", "date", true), true)}
        {frow("Employment Status", <Combo
          value={f.status}
          onChange={v => upd("status", v)}
          options={STATUSES}
          placeholder="Select status"
        />)}
        {frow("CTC (INR/month)", posNumInp(String(f.ctc), v => upd("ctc", v), "0.00", true, CTC_MAX))}
      </G3>
      {frow("Reporting Manager", inp(f.reportingManager, v => upd("reportingManager", v), "Manager name", "text", false, FIELD_MAX.reporting_manager))}
      <div style={{ borderTop: "1px solid #F1F5F9", margin: "16px 0" }} />
      <div style={{ fontSize: 12, fontWeight: 700, color: "#E8520A", marginBottom: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>Skills</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <input className="inp" value={f.skillInput} onChange={e => upd("skillInput", e.target.value)}
          placeholder="Type a skill and press Add" onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
          style={{ flex: 1 }} />
        <button className="btn-secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }} onClick={addSkill}>+ Add Skill</button>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {f.skills.map(s => (
          <span key={s} style={{
            background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 20, padding: "3px 10px", fontSize: 12, color: "#1E40AF",
            display: "flex", alignItems: "center", gap: 5
          }}>
            {s}
            <span style={{ cursor: "pointer", color: "#6B7280", fontWeight: 700 }} onClick={() => delSkill(s)}>&#215;</span>
          </span>
        ))}
      </div>
    </div>,

    // Tab 3 — Education & Work History
    <div key={3}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#E8520A", textTransform: "uppercase", letterSpacing: 0.5 }}>Educational Background</div>
        <button className="btn-secondary" style={{ fontSize: 11 }} onClick={addEdu}>+ Add Qualification</button>
      </div>
      {f.education.map((edu, i) => (
        <div key={i} style={{ background: "#F8FAFC", borderRadius: 10, padding: "14px 16px", marginBottom: 12, border: "1px solid #F1F5F9", position: "relative" }}>
          <button onClick={() => delEdu(i)} style={{
            position: "absolute", top: 10, right: 10, background: "none", border: "none",
            cursor: "pointer", color: "#EF4444", fontSize: 14, fontWeight: 700
          }}>&#215;</button>
          <G2>
            {frow("Degree / Certificate", inp(edu.degree, v => updEdu(i, "degree", v), "B.Tech, MBA, etc."))}
            {frow("Institution / University", inp(edu.institution, v => updEdu(i, "institution", v), "College or university name"))}
          </G2>
          <G3>
            {frow("Year of Passing", digitsOnly(edu.year, v => updEdu(i, "year", v), "YYYY", 4))}
            {frow("Grade (out of 100)", posNumInp(edu.grade, v => updEdu(i, "grade", v), "0–100", true, 100))}
            {frow("Specialization", inp(edu.specialization, v => updEdu(i, "specialization", v), "Subject / stream"))}
          </G3>
        </div>
      ))}
      <div style={{ borderTop: "1px solid #F1F5F9", margin: "16px 0" }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#E8520A", textTransform: "uppercase", letterSpacing: 0.5 }}>Work History</div>
        <button className="btn-secondary" style={{ fontSize: 11 }} onClick={addWork}>+ Add Employer</button>
      </div>
      {f.workHistory.length === 0 && (
        <div style={{ textAlign: "center", color: "#94A3B8", fontSize: 12, padding: "16px 0" }}>No previous work history added. Click "+ Add Employer" to add.</div>
      )}
      {f.workHistory.map((w, i) => (
        <div key={i} style={{ background: "#F8FAFC", borderRadius: 10, padding: "14px 16px", marginBottom: 12, border: "1px solid #F1F5F9", position: "relative" }}>
          <button onClick={() => delWork(i)} style={{
            position: "absolute", top: 10, right: 10, background: "none", border: "none",
            cursor: "pointer", color: "#EF4444", fontSize: 14, fontWeight: 700
          }}>&#215;</button>
          <G2>
            {frow("Company", inp(w.company, v => updWork(i, "company", v), "Employer name"))}
            {frow("Role / Designation", inp(w.role, v => updWork(i, "role", v), "Job title"))}
          </G2>
          <G3>
            {frow("From", inp(w.from, v => updWork(i, "from", v), "", "month"))}
            {frow("To", inp(w.to, v => updWork(i, "to", v), "", "month"))}
            {frow("Last CTC (INR)", posNumInp(w.ctc, v => updWork(i, "ctc", v), "0", true, CTC_MAX))}
          </G3>
          {frow("Reason for Leaving", inp(w.reason, v => updWork(i, "reason", v), "Career growth, relocation, etc."))}
        </div>
      ))}
    </div>,

    // Tab 4 — Notes & Resume
    <div key={4}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#E8520A", marginBottom: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>Resume</div>
      {/* Hidden real file input */}
      <input
        type="file" accept=".pdf,.doc,.docx" style={{ display: "none" }} ref={resumeInputRef}
        onChange={e => {
          const file = e.target.files?.[0];
          if (!file) return;
          if (file.size > 5 * 1024 * 1024) { setFileError("File too large — max 5 MB allowed."); e.target.value = ""; return; }
          setFileError("");
          upd("resumeFile", file.name);
          upd("resumeFileObj", file);
          e.target.value = ""; // reset so same file can be re-selected
        }}
      />
      <div style={{ border: "2px dashed #E2E8F0", borderRadius: 12, padding: "24px", textAlign: "center", background: "#F8FAFC", marginBottom: 20 }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
        {f.resumeFile ? (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#0D1B2A", marginBottom: 6 }}>{f.resumeFile}</div>
            {f.resumeFileObj && <div style={{ fontSize: 11, color: "#10B981", marginBottom: 8 }}>New file selected — will upload on save</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => { upd("resumeFile", ""); upd("resumeFileObj", null); }}>Remove</button>
              <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => resumeInputRef.current?.click()}>Replace File</button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 10 }}>Drag and drop resume file, or click to browse</div>
            <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 12 }}>PDF, DOC, DOCX — max 5MB</div>
            <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => resumeInputRef.current?.click()}>Browse File</button>
          </div>
        )}
      </div>
      {fileError && <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#B91C1C", marginTop: 8 }}>{fileError}</div>}
      <div style={{ fontSize: 12, fontWeight: 700, color: "#E8520A", marginBottom: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>Internal Notes</div>
      <textarea className="inp" rows={5} value={f.notes} onChange={e => upd("notes", e.target.value)}
        placeholder="Internal notes about this employee — performance observations, special arrangements, etc."
        style={{ resize: "vertical", fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6 }} />
      <div style={{ marginTop: 16, background: "#FFF5F0", border: "1px solid #FED7AA", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#92400E" }}>
        Notes are internal only and not visible to the employee or stakeholders.
      </div>
    </div>,

    // Tab 5 — Review & Create
    <div key={5}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#E8520A", marginBottom: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>Summary Preview</div>
      <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "18px 20px", marginBottom: 18 }}>
        {[
          ["Employee Id", f.code || "—"],
          ["Full Name", [f.firstName, f.lastName].filter(Boolean).join(" ") || "—"],
          ["Official Email", f.officialEmail || "—"],
          ["Primary Phone", f.primaryPhone || "—"],
          ["Designation", f.designation || "—"],
          ["Department", f.department || "—"],
          ["DOJ", f.joinDate || "—"],
          ["CTC (INR/month)", f.ctc !== "" && f.ctc !== undefined ? f.ctc : "—"],
          ["Status", f.status || "—"],
          ["Nationality", f.nationality || "—"],
          ["Resume", f.resumeFile || "Not uploaded"],
        ].map(([label, value]) => (
          <div key={label} style={{ display: "flex", alignItems: "flex-start", marginBottom: 8, fontSize: 13 }}>
            <span style={{ color: "#64748B", minWidth: 180, fontWeight: 500 }}>{label}</span>
            <span style={{ color: "#0D1B2A", fontWeight: 600 }}>{value}</span>
          </div>
        ))}
      </div>
      {validationErrors.length > 0 && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "14px 18px", marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#991B1B", marginBottom: 8 }}>Please fix the following before creating:</div>
          {validationErrors.map((e, i) => (
            <div key={i} style={{ fontSize: 12, color: "#DC2626", marginBottom: 4 }}>• {e}</div>
          ))}
        </div>
      )}
    </div>
  ];

  return (
    <div className="modal-overlay" style={{ left: 236 }}>
      <div className="modal" style={{ maxWidth: 700, width: "min(700px, calc(100% - 32px))" }}>
        {/* Header */}
        <div style={{
          padding: "22px 28px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "linear-gradient(135deg,#0D1B2A 0%,#1E3A5F 100%)", borderRadius: "14px 14px 0 0"
        }}>
          <div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#fff" }}>
              {isEdit ? "Edit Employee" : "Add New Employee"}
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,.55)", marginTop: 2 }}>
              {isEdit ? [f.firstName, f.lastName].filter(Boolean).join(" ") + " · " + f.code : "Complete all mandatory fields marked with *"}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,.15)", border: "none", borderRadius: 8, color: "#fff",
            padding: "6px 12px", fontSize: 18, cursor: "pointer"
          }}>&#215;</button>
        </div>

        {/* Progress tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid #F1F5F9", overflowX: "auto" }}>
          {TABS.map((t, i) => (
            <button key={i} onClick={() => setTab(i)} style={{
              flex: 1, minWidth: 120, padding: "12px 10px", border: "none", cursor: "pointer",
              fontSize: 11, fontWeight: tab === i ? 700 : 500,
              background: tab === i ? "#FFF5F0" : "#fff",
              color: tab === i ? "#E8520A" : "#64748B",
              borderBottom: tab === i ? "2.5px solid #E8520A" : "2.5px solid transparent",
              whiteSpace: "nowrap"
            }}>
              {i + 1}. {t}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: "22px 28px", maxHeight: "58vh", overflowY: "auto" }}>
          {tabContent[tab]}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 28px 20px", borderTop: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 8 }}>
            {tab > 0 && <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setTab(t => t - 1)}>&#8592; Back</button>}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            {tab < TABS.length - 1
              ? <button className="btn-secondary" style={{ fontSize: 12, fontWeight: 700 }} onClick={() => setTab(t => t + 1)}>Next &#8594;</button>
              : <button className="btn-primary" disabled={!required} style={{ opacity: required ? 1 : 0.6, cursor: required ? "pointer" : "not-allowed" }} onClick={submit}>{isEdit ? "Save Changes" : "Create Employee"}</button>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Normalize full employee detail from API (DB snake_case → UI camelCase) ───
function normalizeEmployeeDetail(d) {
  const firstName = d.first_name || d.firstName || d.name?.split(" ")[0] || "";
  const lastName = d.last_name || d.lastName || d.name?.split(" ").slice(1).join(" ") || "";
  return {
    ...d,
    // SELECT * from the detail endpoint doesn't include a CONCAT'd "name" column —
    // build it here so Avatar / header / exports don't break.
    name: d.name || [firstName, lastName].filter(Boolean).join(" "),
    status: d.employment_status
      || (d.active != null ? (Number(d.active) ? "Active" : "Inactive") : (d.status || "Active")),
    firstName,
    lastName,
    bloodGroup: d.blood_group || d.bloodGroup || "",
    aadhaar: d.aadhaar_number || d.aadhaar || "",
    pan: d.pan_number || d.pan || "",
    officialEmail: d.official_email || d.officialEmail || "",
    personalEmail: d.personal_email || d.personalEmail || "",
    primaryPhone: d.primary_phone || d.primaryPhone || "",
    secondaryPhone: d.secondary_phone || d.secondaryPhone || "",
    dob: d.dob ? String(d.dob).split("T")[0] : (d.dob || ""),
    designation: d.role || d.designation || "",
    joinDate: d.joining_date
      ? String(d.joining_date).split("T")[0]
      : (d.joinDate || ""),
    notes: d.internal_notes || d.notes || "",
    resumeFile: d.resume_original_name || d.resumeFile || "",
    reportingManager: d.reporting_manager || d.reportingManager || "",
    passport: {
      no: d.passport_number || (d.passport || {}).no || "",
      expiry: d.passport_expiry
        ? String(d.passport_expiry).split("T")[0]
        : ((d.passport || {}).expiry || ""),
      country: d.passport_country || (d.passport || {}).country || "",
    },
    emergency: {
      name: [d.ec_first_name, d.ec_last_name].filter(Boolean).join(" ") || (d.emergency || {}).name || "",
      firstName: d.ec_first_name || (d.emergency || {}).firstName || "",
      lastName: d.ec_last_name || (d.emergency || {}).lastName || "",
      relation: d.ec_relation || (d.emergency || {}).relation || "",
      phone: d.ec_phone || (d.emergency || {}).phone || "",
      email: d.ec_email || (d.emergency || {}).email || "",
    },
    currentAddr: {
      line1: d.curr_addr_line1 || (d.currentAddr || {}).line1 || "",
      line2: d.curr_addr_line2 || (d.currentAddr || {}).line2 || "",
      city: d.curr_addr_city || (d.currentAddr || {}).city || "",
      state: d.curr_addr_state || (d.currentAddr || {}).state || "",
      pin: d.curr_addr_pincode || (d.currentAddr || {}).pin || "",
    },
    permanentAddr: {
      line1: d.perm_addr_line1 || (d.permanentAddr || {}).line1 || "",
      line2: d.perm_addr_line2 || (d.permanentAddr || {}).line2 || "",
      city: d.perm_addr_city || (d.permanentAddr || {}).city || "",
      state: d.perm_addr_state || (d.permanentAddr || {}).state || "",
      pin: d.perm_addr_pincode || (d.permanentAddr || {}).pin || "",
    },
    skills: Array.isArray(d.skills)
      ? d.skills.map(s => (typeof s === "string" ? s : (s.skill_name || s.name || "")))
      : [],
    education: Array.isArray(d.education)
      ? d.education.map(e => ({
        degree: e.degree || "",
        institution: e.institution || "",
        year: e.end_year != null ? String(e.end_year) : (e.year || ""),
        grade: e.grade_cgpa || e.grade || "",
        specialization: e.field_of_study || e.specialization || "",
      }))
      : [],
    workHistory: Array.isArray(d.workHistory)
      ? d.workHistory.map(w => ({
        company: w.company || "",
        role: w.title || w.role || "",
        from: w.start_date ? String(w.start_date).slice(0, 7) : (w.from || ""),
        to: w.end_date ? String(w.end_date).slice(0, 7) : (w.to || ""),
        reason: w.description || w.reason || "",
        ctc: w.ctc || "",
      }))
      : [],
  };
}

// ─── Employee Database Component ──────────────────────────────────────────────

function normalizeEmpListItem(e) {
  return {
    ...e,
    designation: e.designation || e.role || "",
    firstName: e.first_name || "",
    lastName: e.last_name || "",
    officialEmail: e.official_email || e.officialEmail || "",
    personalEmail: e.personal_email || e.personalEmail || "",
    primaryPhone: e.primary_phone || e.primaryPhone || "",
    secondaryPhone: e.secondary_phone || e.secondaryPhone || "",
    gender: e.gender || "",
    dob: e.dob ? String(e.dob).split("T")[0] : (e.dob || ""),
    bloodGroup: e.blood_group || e.bloodGroup || "",
    nationality: e.nationality || "",
    aadhaar: e.aadhaar_number || e.aadhaar || "",
    pan: e.pan_number || e.pan || "",
    joinDate: e.joining_date
      ? String(e.joining_date).split("T")[0]
      : (e.joinDate || ""),
    reportingManager: e.reporting_manager || e.reportingManager || "",
    status: e.employment_status
      || (e.active != null ? (Number(e.active) ? "Active" : "Inactive") : (e.status || "Active")),
    // Always build nested objects from snake_case fields. Earlier code gated on
    // a "primary" sub-field (e.g. passport_number) — which silently dropped
    // partial data (e.g. an employee with only passport_expiry filled) and
    // caused data loss when re-saving the modal.
    currentAddr: {
      line1: e.curr_addr_line1 || (e.currentAddr || {}).line1 || "",
      line2: e.curr_addr_line2 || (e.currentAddr || {}).line2 || "",
      city: e.curr_addr_city || (e.currentAddr || {}).city || "",
      state: e.curr_addr_state || (e.currentAddr || {}).state || "",
      pin: e.curr_addr_pincode || (e.currentAddr || {}).pin || "",
    },
    permanentAddr: {
      line1: e.perm_addr_line1 || (e.permanentAddr || {}).line1 || "",
      line2: e.perm_addr_line2 || (e.permanentAddr || {}).line2 || "",
      city: e.perm_addr_city || (e.permanentAddr || {}).city || "",
      state: e.perm_addr_state || (e.permanentAddr || {}).state || "",
      pin: e.perm_addr_pincode || (e.permanentAddr || {}).pin || "",
    },
    passport: {
      no: e.passport_number || (e.passport || {}).no || "",
      expiry: e.passport_expiry ? String(e.passport_expiry).split("T")[0] : ((e.passport || {}).expiry || ""),
      country: e.passport_country || (e.passport || {}).country || "",
    },
    notes: e.internal_notes || e.notes || "",
    emergency: {
      name: [e.ec_first_name, e.ec_last_name].filter(Boolean).join(" ") || (e.emergency || {}).name || "",
      firstName: e.ec_first_name || (e.emergency || {}).firstName || "",
      lastName: e.ec_last_name || (e.emergency || {}).lastName || "",
      relation: e.ec_relation || (e.emergency || {}).relation || "",
      phone: e.ec_phone || (e.emergency || {}).phone || "",
      email: e.ec_email || (e.emergency || {}).email || "",
    },
    education: Array.isArray(e.education) ? e.education : [],
    workHistory: Array.isArray(e.workHistory) ? e.workHistory : [],
    skills: e.skills_csv
      ? e.skills_csv.split(", ").filter(Boolean)
      : (Array.isArray(e.skills)
        ? e.skills.map(s => typeof s === "string" ? s : (s.skill_name || ""))
        : []),
  };
}

const EmployeeDatabase = ({ topBarProps, empList: empListProp, setEmpList: setEmpListProp, openAddModal, onAddModalClosed, onNewEmployee, currentRole }) => {
  const [empList, setEmpListLocal] = useState(empListProp || []);

  // Sync when parent loads real API data
  // Always reload fresh from server when this page mounts (sidebar navigation)
  useEffect(() => {
    apiFetch("/api/employees?limit=500")
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data) {
          const fresh = d.data.map(normalizeEmpListItem);
          setEmpListLocal(fresh);
          if (setEmpListProp) setEmpListProp(fresh);
        }
      })
      .catch(() => {
        // Fallback to prop data if fetch fails
        if (empListProp && empListProp.length > 0) setEmpListLocal(empListProp);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [search, setSearch] = useState("");
  const [statusFilt, setStatusFilt] = useState("All");
  const [deptFilt, setDeptFilt] = useState("All");
  const [sortBy, setSortBy] = useState("code");
  const [sortDir, setSortDir] = useState("asc");

  // Dropdown quick-sort options
  const SORT_OPTIONS = [
    { value: "name_asc", label: "Name  A → Z", by: "name", dir: "asc" },
    { value: "name_desc", label: "Name  Z → A", by: "name", dir: "desc" },
    { value: "code_asc", label: "Employee Id  A → Z", by: "code", dir: "asc" },
    { value: "code_desc", label: "Employee Id  Z → A", by: "code", dir: "desc" },
    { value: "created_desc", label: "Recently Added", by: "created", dir: "desc" },
    { value: "created_asc", label: "First Added", by: "created", dir: "asc" },
    { value: "joined_desc", label: "DOJ (Newest First)", by: "joined", dir: "desc" },
    { value: "joined_asc", label: "DOJ (Oldest First)", by: "joined", dir: "asc" },
  ];
  const dropdownVal = SORT_OPTIONS.find(o => o.by === sortBy && o.dir === sortDir)?.value || "";
  const applyDropdownSort = val => {
    const opt = SORT_OPTIONS.find(o => o.value === val);
    if (opt) { setSortBy(opt.by); setSortDir(opt.dir); }
  };
  const [showAdd, setShowAdd] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [viewEmp, setViewEmp] = useState(null);
  const [viewTab, setViewTab] = useState(0);
  const [confirmDel, setConfirmDel] = useState(null);
  const [toast, setToast] = useState("");
  const [toastType, setToastType] = useState("");
  const showToast = (msg, type = "") => { setToast(msg); setToastType(type); setTimeout(() => { setToast(""); setToastType(""); }, 2800); };

  // Sync helper — keeps local + lifted state in step
  const setEmpList = updater => {
    setEmpListLocal(updater);
    if (setEmpListProp) setEmpListProp(updater);
  };

  // When AllocationPage's "+ New Employee" button navigates here, auto-open modal
  useEffect(() => {
    if (openAddModal) {
      setShowAdd(true);
      if (onAddModalClosed) onAddModalClosed();
    }
  }, [openAddModal]);

  const depts = ["All", ...new Set(empList.map(e => e.department))];
  const statuses = ["All", "Active", "On Leave", "Probation", "Notice Period", "Inactive"];

  const filtered = empList
    .filter(e => statusFilt === "All" || e.status === statusFilt)
    .filter(e => deptFilt === "All" || e.department === deptFilt)
    .filter(e => !search || (e.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.code || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.designation || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.officialEmail || "").toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === "created") {
        // Sort by DB insertion order using created_at; fall back to array position
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return sortDir === "asc" ? ta - tb : tb - ta;
      }
      let va = "", vb = "";
      if (sortBy === "name") { va = a.name || ""; vb = b.name || ""; }
      else if (sortBy === "code") { va = a.code || ""; vb = b.code || ""; }
      else if (sortBy === "desig") { va = a.designation || ""; vb = b.designation || ""; }
      else if (sortBy === "dept") { va = a.department || ""; vb = b.department || ""; }
      else if (sortBy === "joined") { va = a.joinDate || ""; vb = b.joinDate || ""; }
      else if (sortBy === "status") { va = a.status || ""; vb = b.status || ""; }
      const cmp = va.localeCompare(vb);
      return sortDir === "asc" ? cmp : -cmp;
    });

  const toggleSort = col => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const SortTh = ({ col, children }) => {
    const active = sortBy === col;
    return (
      <th onClick={() => toggleSort(col)} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          {children}
          <span style={{ fontSize: 10, color: active ? "#E8520A" : "#CBD5E1", lineHeight: 1 }}>
            {active ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
          </span>
        </span>
      </th>
    );
  };

  const toSnake = emp => ({
    // Empty-string / undefined / null → SQL NULL.
    // IMPORTANT: never fall back to the snake_case copy of a field that the form
    // also tracks under a camelCase key — the user just cleared the camelCase
    // copy, so the snake_case copy (stale from initial load) must NOT win.
    id: emp.id,
    code: emp.code,
    name: [emp.firstName, emp.lastName].filter(Boolean).join(" ") || emp.name,
    first_name: emp.firstName || emp.name?.split(" ")[0] || null,
    last_name: emp.lastName || emp.name?.split(" ").slice(1).join(" ") || null,
    official_email: nul(emp.officialEmail),
    personal_email: nul(emp.personalEmail),
    primary_phone: nul(emp.primaryPhone),
    secondary_phone: nul(emp.secondaryPhone),
    gender: nul(emp.gender),
    dob: nul(emp.dob),
    blood_group: nul(emp.bloodGroup),
    nationality: nul(emp.nationality),
    aadhaar_number: nul(emp.aadhaar),
    pan_number: nul(emp.pan),
    passport_number: nul((emp.passport || {}).no),
    passport_expiry: nul((emp.passport || {}).expiry),
    passport_country: nul((emp.passport || {}).country),
    curr_addr_line1: nul((emp.currentAddr || {}).line1),
    curr_addr_line2: nul((emp.currentAddr || {}).line2),
    curr_addr_city: nul((emp.currentAddr || {}).city),
    curr_addr_state: nul((emp.currentAddr || {}).state),
    curr_addr_pincode: nul((emp.currentAddr || {}).pin),
    perm_addr_same_as_curr: emp.sameAddr ? 1 : 0,
    perm_addr_line1: nul((emp.permanentAddr || {}).line1),
    perm_addr_line2: nul((emp.permanentAddr || {}).line2),
    perm_addr_city: nul((emp.permanentAddr || {}).city),
    perm_addr_state: nul((emp.permanentAddr || {}).state),
    perm_addr_pincode: nul((emp.permanentAddr || {}).pin),
    ec_first_name: nul((emp.emergency || {}).firstName),
    ec_last_name: nul((emp.emergency || {}).lastName),
    ec_relation: nul((emp.emergency || {}).relation),
    ec_phone: nul((emp.emergency || {}).phone),
    ec_email: nul((emp.emergency || {}).email),
    role: nul(emp.designation),
    department: nul(emp.department),
    joining_date: nul(emp.joinDate),
    employment_status: emp.status || "Active",
    ctc: (emp.ctc !== undefined && emp.ctc !== "" && emp.ctc !== null) ? Number(emp.ctc) : 0,
    reporting_manager: nul(emp.reportingManager),
    internal_notes: nul(emp.notes),
    skills: Array.isArray(emp.skills) ? emp.skills : [],
    education: Array.isArray(emp.education) ? emp.education : [],
    workHistory: Array.isArray(emp.workHistory) ? emp.workHistory : [],
  });

  // Reload employee list fresh from server — called after any create/update/delete
  const reloadEmpList = async () => {
    try {
      const res = await apiFetch("/api/employees?limit=500");
      const d = await res.json();
      if (d.success && d.data) setEmpList(d.data.map(normalizeEmpListItem));
    } catch (e) { /* silently ignore reload errors */ }
  };

  const saveEmp = async emp => {
    const isNew = !empList.find(e => e.id === emp.id);
    if (!isNew) {
      // UPDATE — no optimistic UI, wait for server
      try {
        const res = await apiFetch(`/api/employees/${emp.id}`, { method: "PUT", body: JSON.stringify(toSnake(emp)) });
        const d = await res.json();
        if (!d.success) { showToast("Error: " + (d.message || "Update failed"), "error"); return; }
        // Upload resume file if a new one was selected
        if (emp.resumeFileObj) {
          const fd = new FormData(); fd.append("file", emp.resumeFileObj);
          await apiFetch(`/api/employees/${emp.id}/resume`, { method: "POST", body: fd, isFormData: true }).catch(() => { });
        }
        setShowAdd(false); setEditEmp(null);
        await reloadEmpList();
        showToast(emp.name + " updated successfully", "#10B981");
      } catch (e) { showToast("Network error — update not saved", "error"); }
    } else {
      // CREATE — no optimistic UI, wait for server
      try {
        const res = await apiFetch("/api/employees", { method: "POST", body: JSON.stringify(toSnake(emp)) });
        const d = await res.json();
        if (!d.success) {
          const msg = d.message || "Add failed";
          const friendly = msg.includes("Duplicate entry") && msg.includes("code") ? "Employee Id already exists — please use a different Id." : msg.includes("cannot be null") ? msg.replace("Column '", "").replace("' cannot be null", "") + " is required." : msg;
          showToast("Error: " + friendly, "error");
          return;
        }
        const newId = d.data?.id || d.id || emp.id;
        // Upload resume file if selected during create
        if (emp.resumeFileObj) {
          const fd = new FormData(); fd.append("file", emp.resumeFileObj);
          await apiFetch(`/api/employees/${newId}/resume`, { method: "POST", body: fd, isFormData: true }).catch(() => { });
        }
        setShowAdd(false); setEditEmp(null);
        await reloadEmpList();
        if (onNewEmployee) onNewEmployee({ ...emp, id: newId });
        showToast(emp.name + " added — visible in Allocation & Leakage as Unallocated", "#10B981");
      } catch (e) { showToast("Network error — employee not saved", "error"); }
    }
  };
  const deleteEmp = async id => {
    const e = empList.find(x => x.id === id);
    setConfirmDel(null);
    setViewEmp(null);
    try {
      const res = await apiFetch(`/api/employees/${id}`, { method: "DELETE" });
      const d = await res.json();
      if (!d.success) { showToast("Error: " + (d.message || "Delete failed"), "error"); return; }
      await reloadEmpList();
      showToast((e ? e.name : "Employee") + " marked as Inactive", "#10B981");
    } catch (err) { showToast("Network error — delete not completed", "error"); }
  };

  const statColor = s => ({ Active: "#10B981", "On Leave": "#F59E0B", Probation: "#3B82F6", "Notice Period": "#EF4444", Inactive: "#94A3B8" }[s] || "#94A3B8");
  const statBg = s => ({ Active: "#F0FDF4", "On Leave": "#FFFBEB", Probation: "#EFF6FF", "Notice Period": "#FEF2F2", Inactive: "#F8FAFC" }[s] || "#F8FAFC");

  const VIEW_TABS = ["Overview", "Contact & Address", "Professional", "Education & History", "Documents"];

  // ── Hidden file input ref for XLS import ────────────────────────────────────
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // { inserted, total, parseErrors, insertErrors }
  const [pendingImportFile, setPendingImportFile] = useState(null); // file selected but not yet confirmed

  // ── Load ExcelJS from CDN (once) — needed for cell colour/styling ────────────
  const loadExcelJS = () => new Promise((resolve, reject) => {
    if (window.ExcelJS) { resolve(window.ExcelJS); return; }
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js";
    s.onload = () => resolve(window.ExcelJS);
    s.onerror = reject;
    document.head.appendChild(s);
  });

  // ── Template download — generated server-side (password protection requires server env) ──
  const downloadTemplate = async () => {
    showToast("Preparing template…", "");
    try {
      const res = await apiFetch("/api/employees/template");
      if (!res.ok) { showToast("Template download failed — please try again", "error"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "Employee_Import_Template.xlsx";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      showToast("Template downloaded — orange = required, ▼ columns have dropdowns", "#10B981");
    } catch (err) {
      console.error("Template download error:", err);
      showToast("Template download failed — please try again", "error");
    }
  };

  // ── Export filtered employees to Excel ───────────────────────────────────────
  // Uses the same column set, header text, and styling as the bulk-import template
  // so the file is round-trippable: download, edit, re-import via the same flow.
  const exportXLS = async () => {
    if (!filtered.length) { showToast("No employees to export", "error"); return; }
    showToast("Generating Excel…", "");
    try {
      const ExcelJS = await loadExcelJS();
      const wb = new ExcelJS.Workbook();
      wb.creator = "Dolluz Corp EPR";
      const ws = wb.addWorksheet("Employees");

      writeTemplateStyledSheet(ws, filtered.map(empToTemplateRow));
      attachEmployeeDropdownsAndValidations(wb, ws, filtered.length);

      // Summary footer row
      const footerRowIdx = filtered.length + 2;
      const footerRow = ws.getRow(footerRowIdx);
      footerRow.height = 16;
      const footerCell = footerRow.getCell(1);
      const filterLabel = [search && `Search: "${search}"`, deptFilt !== "All" && deptFilt, statusFilt !== "All" && statusFilt].filter(Boolean).join(" · ") || "All Employees";
      footerCell.value = `${filtered.length} employee${filtered.length !== 1 ? "s" : ""}  ·  Filter: ${filterLabel}  ·  Exported: ${new Date().toLocaleString()}  ·  Dolluz Corp EPR`;
      footerCell.font = { italic: true, size: 8, color: { argb: "FF94A3B8" } };
      // mergeCells takes numeric (row,col,row,col) — handles columns past Z correctly
      ws.mergeCells(footerRowIdx, 1, footerRowIdx, TEMPLATE_HEADERS.length);

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const filters = [search && `search_${search}`, deptFilt !== "All" && deptFilt, statusFilt !== "All" && statusFilt].filter(Boolean).join("_") || "all";
      const a = document.createElement("a"); a.href = url;
      a.download = `Employees_${filters}_${todayIST()}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      showToast(`Exported ${filtered.length} employee${filtered.length !== 1 ? "s" : ""} to Excel`, "#10B981");
    } catch (err) {
      console.error("Export XLS error:", err);
      showToast("Excel export failed — please try again", "error");
    }
  };

  // ── Load jsPDF + autoTable from CDN (once) ───────────────────────────────────
  const loadJsPDF = () => new Promise((resolve, reject) => {
    if (window.jspdf?.jsPDF) { resolve(window.jspdf.jsPDF); return; }
    const s1 = document.createElement("script");
    s1.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    s1.onload = () => {
      const s2 = document.createElement("script");
      s2.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js";
      s2.onload = () => resolve(window.jspdf.jsPDF);
      s2.onerror = reject;
      document.head.appendChild(s2);
    };
    s1.onerror = reject;
    document.head.appendChild(s1);
  });

  // ── Export filtered employees to PDF (direct download, no print dialog) ──────
  const exportPDF = async () => {
    if (!filtered.length) { showToast("No employees to export", "error"); return; }
    showToast("Generating PDF…", "");
    try {
      const JsPDF = await loadJsPDF();
      const doc = new JsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

      // Header bar
      doc.setFillColor(232, 82, 10);
      doc.rect(0, 0, 297, 16, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("Employee Database Export", 10, 10.5);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");

      const filterLabel = [
        search && `Search: "${search}"`,
        deptFilt !== "All" && `Dept: ${deptFilt}`,
        statusFilt !== "All" && `Status: ${statusFilt}`,
      ].filter(Boolean).join("  ·  ") || "All Employees";
      doc.text(`${filterLabel}  ·  ${filtered.length} employee${filtered.length !== 1 ? "s" : ""}  ·  Generated: ${new Date().toLocaleString()}`, 10, 14.5);

      // Status colour helper
      const statusColour = s => ({
        "Active": { r: 209, g: 250, b: 229 },
        "On Leave": { r: 254, g: 243, b: 199 },
        "Probation": { r: 219, g: 234, b: 254 },
        "Notice Period": { r: 254, g: 226, b: 226 },
        "Inactive": { r: 241, g: 245, b: 249 },
      }[s] || { r: 241, g: 245, b: 249 });

      const statusTextColour = s => ({
        "Active": [6, 95, 70],
        "On Leave": [146, 64, 14],
        "Probation": [30, 64, 175],
        "Notice Period": [153, 27, 27],
        "Inactive": [100, 116, 139],
      }[s] || [100, 116, 139]);

      // ── Shared table styles ───────────────────────────────────────────────────
      const headStyles = { fillColor: [232, 82, 10], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.5 };
      const bodyStyles = { fontSize: 7.5, textColor: [13, 27, 42] };
      const altRow = { fillColor: [248, 250, 252] };
      const margin = { left: 8, right: 8 };

      // ── Section label helper ──────────────────────────────────────────────────
      const sectionLabel = (label, y) => {
        doc.setFillColor(13, 27, 42);
        doc.rect(8, y, 281, 7, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.text(label, 12, y + 5);
      };

      // ── Section 1: Employment details ─────────────────────────────────────────
      sectionLabel("Section 1 — Employment Details", 20);
      doc.autoTable({
        startY: 29,
        head: [["Employee Id", "Name", "Designation", "Department", "Official Email", "Phone", "DOJ", "Status", "CTC (INR/mo)", "Reporting Manager"]],
        body: filtered.map(e => [
          e.code || "",
          e.name || "",
          e.designation || "",
          e.department || "",
          e.officialEmail || "",
          e.primaryPhone || "",
          e.joinDate ? String(e.joinDate).slice(0, 10) : "",
          e.status || "",
          e.ctc ? String(e.ctc) : "",
          e.reportingManager || "",
        ]),
        headStyles,
        bodyStyles,
        alternateRowStyles: altRow,
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 26, fontStyle: "bold" },
          2: { cellWidth: 30 },
          3: { cellWidth: 22 },
          4: { cellWidth: 50 },
          5: { cellWidth: 22 },
          6: { cellWidth: 22 },
          7: { cellWidth: 20 },
          8: { cellWidth: 24 },
          9: { cellWidth: 25 },
        },
        // Prevent "ActiveActive" — clear text before autoTable draws, badge after
        willDrawCell: (data) => {
          if (data.section === "body" && data.column.index === 7) data.cell.text = [];
        },
        didDrawCell: (data) => {
          if (data.section === "body" && data.column.index === 7) {
            const status = data.cell.raw;
            const { r, g, b } = statusColour(status);
            const [tr, tg, tb] = statusTextColour(status);
            const { x, y, width: w, height: h } = data.cell;
            doc.setFillColor(r, g, b);
            doc.roundedRect(x + 1, y + 1.5, w - 2, h - 3, 2, 2, "F");
            doc.setTextColor(tr, tg, tb);
            doc.setFontSize(6.5);
            doc.setFont("helvetica", "bold");
            doc.text(status, x + w / 2, y + h / 2 + 0.5, { align: "center", baseline: "middle" });
          }
        },
        margin,
      });

      // ── Section 2: Personal & contact details ────────────────────────────────
      const sec2Y = doc.lastAutoTable.finalY + 6;
      sectionLabel("Section 2 — Personal & Contact Details", sec2Y);
      doc.autoTable({
        startY: sec2Y + 9,
        head: [["Employee Id", "Name", "Gender", "Date of Birth", "Blood Group", "Nationality", "Aadhaar", "PAN", "Current Address", "Emergency Contact", "Skills"]],
        body: filtered.map(e => {
          const addr = [(e.currentAddr || {}).line1, (e.currentAddr || {}).line2, (e.currentAddr || {}).city, (e.currentAddr || {}).state, (e.currentAddr || {}).pin].filter(Boolean).join(", ");
          const ec = e.emergency?.name ? `${e.emergency.name} (${e.emergency.relation}) ${e.emergency.phone}` : "";
          return [
            e.code || "",
            e.name || "",
            e.gender || "",
            e.dob ? String(e.dob).slice(0, 10) : "",
            e.bloodGroup || "",
            e.nationality || "",
            e.aadhaar || "",
            e.pan || "",
            addr,
            ec,
            Array.isArray(e.skills) ? e.skills.join(", ") : "",
          ];
        }),
        headStyles,
        bodyStyles,
        alternateRowStyles: altRow,
        columnStyles: {
          0: { cellWidth: 18 },
          1: { cellWidth: 24, fontStyle: "bold" },
          2: { cellWidth: 14 },
          3: { cellWidth: 20 },
          4: { cellWidth: 18 },
          5: { cellWidth: 18 },
          6: { cellWidth: 26 },
          7: { cellWidth: 20 },
          8: { cellWidth: 46, overflow: "linebreak" },
          9: { cellWidth: 36 },
          10: { cellWidth: 24 },
        },
        margin,
      });

      // ── Footer with page numbers ──────────────────────────────────────────────
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setTextColor(148, 163, 184);
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.text(
          `Page ${i} of ${pageCount}  ·  Dolluz Corp EPR`,
          297 / 2, 207,
          { align: "center" }
        );
      }

      const fname = `Employees_${(filterLabel.replace(/[^a-zA-Z0-9]/g, "_").slice(0, 30))}_${todayIST()}.pdf`;
      applyPdfWatermark(doc, await loadWatermarkDataUrl());
      doc.save(fname);
      showToast(`PDF downloaded — ${filtered.length} employee${filtered.length !== 1 ? "s" : ""}`, "#10B981");
    } catch (err) {
      console.error("PDF export error:", err);
      showToast("PDF generation failed — please try again", "error");
    }
  };

  // ── Bulk import from uploaded Excel ─────────────────────────────────────────
  // Called when user selects a file — just stage it, don't import yet
  const onImportFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (importInputRef.current) importInputRef.current.value = ""; // reset so same file can be re-selected
    if (!file) return;
    setPendingImportFile(file);
  };

  // Called when user confirms the import in the confirmation modal
  const handleImportXLS = async (file) => {
    if (!file) return;
    setPendingImportFile(null);
    setImporting(true);
    showToast("Importing employees…", "");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiFetch("/api/employees/import", { method: "POST", body: fd, isFormData: true });
      const d = await res.json();
      if (!d.success) { showToast("Import failed: " + (d.message || "Unknown error"), "error"); return; }

      const { inserted = 0, updated = 0, total = 0, parseErrors = [], insertErrors = [] } = d;
      const errCount = parseErrors.length + insertErrors.length;

      // Reload employee list from server using the same normaliser as the initial
      // load — keeps status (employment_status), blood group, gender, etc. in sync
      // without requiring a manual page refresh.
      await reloadEmpList();

      // Always show result modal — so user knows exactly what happened
      setImportResult({ inserted, updated, total, parseErrors, insertErrors, filename: file.name });
    } catch (err) {
      showToast("Network error during import — please try again", "error");
    } finally {
      setImporting(false);
    }
  };

  const bloodStats = () => {
    const bg = {};
    empList.forEach(e => { if (e.bloodGroup) bg[e.bloodGroup] = (bg[e.bloodGroup] || 0) + 1; });
    return bg;
  };

  return (
    <div className="fade-in">
      <TopBar title="Employee Database" subtitle="Full HR records — add, edit, export and manage all employees" {...topBarProps} />

      {/* ── Stats bar ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10, marginBottom: 18 }}>
        {[
          { label: "Total Employees", value: empList.length, color: "#0D1B2A", bg: "#F8FAFC" },
          { label: "Active", value: empList.filter(e => e.status !== "Inactive").length, color: "#10B981", bg: "#F0FDF4" },
          { label: "Departments", value: new Set(empList.map(e => e.department).filter(Boolean)).size, color: "#3B82F6", bg: "#EFF6FF" },
          { label: "With Resume", value: empList.filter(e => e.resumeFile).length, color: "#8B5CF6", bg: "#F5F3FF" },
          { label: "Unallocated", value: empList.filter(e => e.status !== "Inactive" && !Number(e.total_allocation_pct)).length, color: "#EF4444", bg: "#FEF2F2" },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: "12px 16px", border: "1px solid #F1F5F9" }}>
            <div style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>{s.label}</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div className="card" style={{ padding: "14px 20px", marginBottom: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search name, ID, email, designation..." style={{ maxWidth: 280, flex: 1 }} />
        <select className="inp" value={deptFilt} onChange={e => setDeptFilt(e.target.value)} style={{ maxWidth: 160 }}>
          {depts.map(d => <option key={d}>{d}</option>)}
        </select>
        <select className="inp" value={statusFilt} onChange={e => setStatusFilt(e.target.value)} style={{ maxWidth: 140 }}>
          {statuses.map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="inp" value={dropdownVal} onChange={e => applyDropdownSort(e.target.value)}
          style={{ maxWidth: 180, color: dropdownVal ? "#0D1B2A" : "#94A3B8" }}
          title="Sort order">
          <option value="">⇅ Sort by…</option>
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* Hidden file input for XLS import */}
          <input ref={importInputRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={onImportFileSelected} />
          <button className="btn-secondary" style={{ fontSize: 12 }} onClick={downloadTemplate} title="Download fillable import template">
            📋 Template
          </button>
          <button
            className="btn-secondary"
            style={{ fontSize: 12, opacity: importing ? 0.6 : 1, cursor: importing ? "not-allowed" : "pointer" }}
            onClick={() => !importing && importInputRef.current?.click()}
            title="Bulk import employees from filled Excel template"
          >
            {importing ? "⏳ Importing…" : "📤 Import XLS"}
          </button>
          <button className="btn-secondary" style={{ fontSize: 12, color: "#10B981", borderColor: "#A7F3D0" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#10B981"; e.currentTarget.style.background = "#F0FDF4"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#A7F3D0"; e.currentTarget.style.background = "#fff"; }}
            onClick={exportXLS} title={`Export ${filtered.length} filtered employee(s) to Excel`}>
            📊 Export XLS
          </button>
          <button className="btn-secondary" style={{ fontSize: 12, color: "#EF4444", borderColor: "#FECACA" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#EF4444"; e.currentTarget.style.background = "#FEF2F2"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#FECACA"; e.currentTarget.style.background = "#fff"; }}
            onClick={exportPDF} title={`Export ${filtered.length} filtered employee(s) to PDF`}>
            📄 Export PDF
          </button>
          {currentRole !== "Sub-Admin" && <button className="btn-primary" style={{ fontSize: 12 }} onClick={() => setShowAdd(true)}>+ Add Employee</button>}
        </div>
      </div>

      {/* ── Blood Group Mini Chart ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {Object.entries(bloodStats()).map(([bg, cnt]) => (
          <div key={bg} style={{
            background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "6px 12px",
            display: "flex", alignItems: "center", gap: 6, fontSize: 12
          }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#EF4444", flexShrink: 0 }} />
            <strong>{bg}</strong> <span style={{ color: "#94A3B8" }}>({cnt})</span>
          </div>
        ))}
        <div style={{ marginLeft: "auto", fontSize: 11, color: "#94A3B8", alignSelf: "center" }}>
          Blood group distribution · {empList.length} employee{empList.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* ── Employee Table ── */}
      <div className="card">
        <div style={{ overflowX: "auto" }}>
          <table style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <SortTh col="code">Employee Id</SortTh>
                <SortTh col="name">Employee</SortTh>
                <SortTh col="desig">Designation</SortTh>
                <SortTh col="dept">Department</SortTh>
                <th>Contact</th>
                <SortTh col="joined">DOJ</SortTh>
                <SortTh col="status">Status</SortTh>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.id} style={{ cursor: "pointer" }} onClick={() => {
                  setViewTab(0);
                  // Fetch full employee data (includes passport, emergency, education, skills)
                  apiFetch(`/api/employees/${e.id}`)
                    .then(r => r.json())
                    .then(d => setViewEmp(d.success && d.data ? normalizeEmployeeDetail(d.data) : e))
                    .catch(() => setViewEmp(e));
                }}>
                  <td style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "#475569" }}>{e.code}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Avatar name={e.name} size={34} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{e.name}</div>
                        <div style={{ fontSize: 11, color: "#94A3B8" }}>{e.bloodGroup && <span style={{ color: "#EF4444", fontWeight: 600 }}>{e.bloodGroup} · </span>}{e.gender}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: "#475569" }}>{e.designation}</td>
                  <td><span style={{ background: "#F1F5F9", borderRadius: 6, padding: "2px 8px", fontSize: 11, color: "#475569", fontWeight: 500 }}>{e.department}</span></td>
                  <td>
                    <div style={{ fontSize: 12, color: "#475569" }}>{e.primaryPhone}</div>
                    <div style={{ fontSize: 11, color: "#94A3B8" }}>{e.officialEmail}</div>
                  </td>
                  <td style={{ fontSize: 12, color: "#475569", fontFamily: "'JetBrains Mono',monospace" }}>{e.joinDate}</td>
                  <td>
                    <span style={{ background: statBg(e.status), color: statColor(e.status), borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>
                      {e.status}
                    </span>
                  </td>
                  {currentRole !== "Sub-Admin" && (
                    <td onClick={ev => ev.stopPropagation()}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => {
                          apiFetch(`/api/employees/${e.id}`)
                            .then(r => r.json())
                            .then(d => setEditEmp(d.success && d.data ? normalizeEmployeeDetail(d.data) : e))
                            .catch(() => setEditEmp(e));
                        }}>Edit</button>
                        <button className="btn-ghost" style={{ fontSize: 11, color: "#EF4444" }} onClick={() => setConfirmDel(e.id)}>Delete</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 24px", color: "#94A3B8", fontSize: 13 }}>
              No employees match your filters.
            </div>
          )}
        </div>
      </div>

      {/* ── View Employee Profile Modal ── */}
      {viewEmp && (
        <div className="modal-overlay" style={{ left: 236 }}>
          <div className="modal" style={{ maxWidth: 680, width: "min(680px, calc(100% - 32px))" }}>
            {/* Header */}
            <div style={{
              padding: "22px 28px", background: "linear-gradient(135deg,#0D1B2A,#1E3A5F)", borderRadius: "14px 14px 0 0",
              display: "flex", alignItems: "center", gap: 16
            }}>
              <Avatar name={viewEmp.name} size={52} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#fff" }}>{viewEmp.name}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,.6)", marginTop: 2 }}>{viewEmp.designation} · {viewEmp.department}</div>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <span style={{ background: "rgba(255,255,255,.15)", color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>{viewEmp.code}</span>
                  <span style={{ background: statBg(viewEmp.status), color: statColor(viewEmp.status), borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>{viewEmp.status}</span>
                  {viewEmp.bloodGroup && <span style={{ background: "rgba(239,68,68,.2)", color: "#FCA5A5", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>{viewEmp.bloodGroup}</span>}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <button className="btn-secondary" style={{ fontSize: 11 }} onClick={() => { setViewEmp(null); setEditEmp(viewEmp); }}>Edit Profile</button>
                <button onClick={() => setViewEmp(null)} style={{
                  background: "rgba(255,255,255,.15)", border: "none", borderRadius: 8,
                  color: "#fff", padding: "6px 14px", fontSize: 12, cursor: "pointer"
                }}>Close</button>
              </div>
            </div>
            {/* Tabs */}
            <div style={{ display: "flex", borderBottom: "1px solid #F1F5F9", overflowX: "auto" }}>
              {VIEW_TABS.map((t, i) => (
                <button key={i} onClick={() => setViewTab(i)} style={{
                  flex: 1, minWidth: 110, padding: "11px 8px", border: "none",
                  cursor: "pointer", fontSize: 11, fontWeight: viewTab === i ? 700 : 500,
                  background: viewTab === i ? "#FFF5F0" : "#fff", color: viewTab === i ? "#E8520A" : "#64748B",
                  borderBottom: viewTab === i ? "2.5px solid #E8520A" : "2.5px solid transparent", whiteSpace: "nowrap"
                }}>{t}</button>
              ))}
            </div>
            <div style={{ padding: "20px 28px", maxHeight: "55vh", overflowY: "auto" }}>
              {viewTab === 0 && (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {[
                      ["Date of Birth", viewEmp.dob || "—"],
                      ["Gender", viewEmp.gender || "—"],
                      ["Nationality", viewEmp.nationality || "—"],
                      ["Aadhaar", viewEmp.aadhaar || "—"],
                      ["PAN", viewEmp.pan || "—"],
                      ["Passport No.", (viewEmp.passport || {}).no || "—"],
                      ["Passport Expiry", (viewEmp.passport || {}).expiry || "—"],
                      ["DOJ", viewEmp.joinDate || "—"],
                      ["Reporting Manager", viewEmp.reportingManager || "—"],
                      ["CTC", viewEmp.ctc ? "₹" + viewEmp.ctc + "/mo" : "—"],
                    ].map(([l, v]) => (
                      <div key={l} style={{ background: "#F8FAFC", borderRadius: 8, padding: "10px 14px" }}>
                        <div style={{ fontSize: 10, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>{l}</div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "#0D1B2A" }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  {(viewEmp.skills || []).length > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 11, color: "#64748B", fontWeight: 600, marginBottom: 8 }}>SKILLS</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {(viewEmp.skills || []).map(s => <span key={s} style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 20, padding: "3px 10px", fontSize: 12, color: "#1E40AF" }}>{s}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {viewTab === 1 && (
                <div>
                  {[
                    ["Primary Phone", viewEmp.primaryPhone],
                    ["Secondary Phone", viewEmp.secondaryPhone || "—"],
                    ["Official Email", viewEmp.officialEmail],
                    ["Personal Email", viewEmp.personalEmail || "—"],
                    ["Emergency Contact", viewEmp.emergency ? (viewEmp.emergency.name + " (" + viewEmp.emergency.relation + ") · " + viewEmp.emergency.phone) : "—"],
                  ].map(([l, v]) => (
                    <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #F8FAFC" }}>
                      <span style={{ fontSize: 12, color: "#94A3B8", fontWeight: 600 }}>{l}</span>
                      <span style={{ fontSize: 13, color: "#0D1B2A", fontWeight: 500 }}>{v}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 8 }}>Current Address</div>
                    <div style={{ fontSize: 13, color: "#0D1B2A", lineHeight: 1.8 }}>
                      {[(viewEmp.currentAddr || {}).line1, (viewEmp.currentAddr || {}).line2, (viewEmp.currentAddr || {}).city, (viewEmp.currentAddr || {}).state, (viewEmp.currentAddr || {}).pin].filter(Boolean).join(", ")}
                    </div>
                  </div>
                </div>
              )}
              {viewTab === 2 && (
                <div>
                  {[
                    ["Designation", viewEmp.designation],
                    ["Department", viewEmp.department],
                    ["DOJ", viewEmp.joinDate],
                    ["Status", viewEmp.status],
                    ["CTC", viewEmp.ctc ? "₹" + viewEmp.ctc + "/month" : "—"],
                    ["Reporting Manager", viewEmp.reportingManager || "—"],
                  ].map(([l, v]) => (
                    <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #F8FAFC" }}>
                      <span style={{ fontSize: 12, color: "#94A3B8", fontWeight: 600 }}>{l}</span>
                      <span style={{ fontSize: 13, color: "#0D1B2A", fontWeight: 500 }}>{v}</span>
                    </div>
                  ))}
                  {viewEmp.notes && (
                    <div style={{ marginTop: 16, background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "12px 14px" }}>
                      <div style={{ fontSize: 11, color: "#92400E", fontWeight: 700, marginBottom: 4 }}>INTERNAL NOTES</div>
                      <div style={{ fontSize: 13, color: "#78350F" }}>{viewEmp.notes}</div>
                    </div>
                  )}
                </div>
              )}
              {viewTab === 3 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 10 }}>Education</div>
                  {(viewEmp.education || []).map((ed, i) => (
                    <div key={i} style={{ background: "#F8FAFC", borderRadius: 8, padding: "12px 14px", marginBottom: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "#0D1B2A" }}>{ed.degree} — {ed.institution}</div>
                      <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>{ed.specialization} · {ed.year} · {ed.grade}</div>
                    </div>
                  ))}
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 10, marginTop: 16 }}>Work History</div>
                  {(viewEmp.workHistory || []).length === 0 && <div style={{ fontSize: 12, color: "#94A3B8" }}>No previous employment on record.</div>}
                  {(viewEmp.workHistory || []).map((w, i) => (
                    <div key={i} style={{ background: "#F8FAFC", borderRadius: 8, padding: "12px 14px", marginBottom: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "#0D1B2A" }}>{w.company} — {w.role}</div>
                      <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>{w.from} to {w.to} · CTC: ₹{w.ctc}</div>
                      {w.reason && <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>Reason: {w.reason}</div>}
                    </div>
                  ))}
                </div>
              )}
              {viewTab === 4 && (
                <div>
                  <div style={{ background: "#F8FAFC", borderRadius: 12, padding: "20px", textAlign: "center", marginBottom: 16 }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#0D1B2A", marginBottom: 4 }}>{viewEmp.resumeFile || "No resume uploaded"}</div>
                    {viewEmp.resumeFile && (
                      <button className="btn-secondary" style={{ fontSize: 12, marginTop: 8 }} onClick={async () => {
                        try {
                          const res = await apiFetch(`/api/employees/${viewEmp.id}/resume`);
                          if (!res.ok) { showToast("Resume file not found on server"); return; }
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url; a.download = viewEmp.resumeFile;
                          document.body.appendChild(a); a.click();
                          document.body.removeChild(a); URL.revokeObjectURL(url);
                        } catch { showToast("Download failed — please try again"); }
                      }}>
                        Download Resume
                      </button>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                    <button className="btn-ghost" style={{ fontSize: 12 }} onClick={async () => {
                      const e = viewEmp;
                      const fullName = e.name || [e.firstName, e.lastName].filter(Boolean).join(" ") || "Profile";
                      showToast("Generating Excel…", "");
                      try {
                        const ExcelJS = await loadExcelJS();
                        const wb = new ExcelJS.Workbook();
                        wb.creator = "Dolluz Corp EPR";
                        const ws = wb.addWorksheet("Profile");
                        writeTemplateStyledSheet(ws, [empToTemplateRow(e)]);
                        attachEmployeeDropdownsAndValidations(wb, ws, 1);

                        const buf = await wb.xlsx.writeBuffer();
                        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                        const url = URL.createObjectURL(blob);
                        const safeName = fullName.replace(/\s+/g, "_");
                        const a = document.createElement("a"); a.href = url; a.download = `${e.code || "employee"}_${safeName}.xlsx`;
                        document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                        showToast(`${fullName}'s profile exported to Excel`, "#10B981");
                      } catch (err) {
                        console.error("Profile Excel export error:", err);
                        showToast("Excel export failed — please try again", "error");
                      }
                    }}>📊 Export to Excel</button>
                    <button className="btn-ghost" style={{ fontSize: 12 }} onClick={async () => {
                      const e = viewEmp;
                      const fullName = e.name || [e.firstName, e.lastName].filter(Boolean).join(" ") || "Employee Profile";
                      showToast("Generating profile PDF…", "");
                      try {
                        const JsPDF = await loadJsPDF();
                        const doc = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

                        // Header bar
                        doc.setFillColor(232, 82, 10);
                        doc.rect(0, 0, 210, 18, "F");
                        doc.setTextColor(255, 255, 255);
                        doc.setFontSize(13);
                        doc.setFont("helvetica", "bold");
                        doc.text(fullName, 10, 11);
                        doc.setFontSize(8);
                        doc.setFont("helvetica", "normal");
                        doc.text(`${e.code || ""}  ·  ${e.designation || ""}  ·  ${e.department || ""}`, 10, 16);

                        const section = (title, yStart) => {
                          doc.setFillColor(248, 250, 252);
                          doc.rect(10, yStart, 190, 6, "F");
                          doc.setTextColor(232, 82, 10);
                          doc.setFontSize(7.5);
                          doc.setFont("helvetica", "bold");
                          doc.text(title.toUpperCase(), 13, yStart + 4);
                          return yStart + 8;
                        };
                        const field = (label, value, y, x = 10, colW = 190) => {
                          if (!value) return y;
                          doc.setTextColor(148, 163, 184);
                          doc.setFontSize(7);
                          doc.setFont("helvetica", "bold");
                          doc.text(label, x + 3, y);
                          doc.setTextColor(13, 27, 42);
                          doc.setFont("helvetica", "normal");
                          const lines = doc.splitTextToSize(String(value), colW - 55);
                          doc.text(lines, x + 55, y);
                          doc.setDrawColor(241, 245, 249);
                          doc.line(x, y + 1.5, x + colW, y + 1.5);
                          return y + (lines.length * 4) + 1;
                        };

                        let y = 24;
                        y = section("Personal Information", y);
                        y = field("Gender", e.gender, y); y = field("Date of Birth", e.dob, y);
                        y = field("Blood Group", e.bloodGroup, y); y = field("Nationality", e.nationality, y);
                        y = field("Aadhaar", e.aadhaar, y); y = field("PAN", e.pan, y);
                        y += 3;
                        y = section("Contact Details", y);
                        y = field("Primary Phone", e.primaryPhone, y); y = field("Official Email", e.officialEmail, y);
                        y = field("Personal Email", e.personalEmail, y);
                        const currAddr = [e.currentAddr?.line1, e.currentAddr?.line2, e.currentAddr?.city, e.currentAddr?.state, e.currentAddr?.pin].filter(Boolean).join(", ");
                        if (currAddr) y = field("Current Address", currAddr, y);
                        if (e.emergency?.name) y = field("Emergency Contact", `${e.emergency.name} (${e.emergency.relation})  ·  ${e.emergency.phone}`, y);
                        y += 3;
                        y = section("Professional", y);
                        y = field("Designation", e.designation, y); y = field("Department", e.department, y);
                        y = field("DOJ", e.joinDate, y); y = field("Status", e.status, y);
                        // ₹ U+20B9 doesn't render in jsPDF's default Helvetica font — use "INR" instead.
                        if (e.ctc) y = field("CTC", `INR ${e.ctc} / month`, y);
                        if (e.reportingManager) y = field("Reporting Manager", e.reportingManager, y);
                        if ((e.education || []).length) {
                          y += 3; y = section("Education", y);
                          (e.education || []).forEach(ed => { y = field(ed.degree || "Degree", `${ed.institution}${ed.year ? "  ·  " + ed.year : ""}${ed.grade ? "  ·  " + ed.grade : ""}`, y); });
                        }
                        if (Array.isArray(e.skills) && e.skills.length) {
                          y += 3; y = section("Skills", y);
                          y = field("Skills", e.skills.join(", "), y);
                        }
                        if (e.notes) {
                          y += 3; y = section("Internal Notes", y);
                          y = field("Notes", e.notes, y);
                        }

                        // Footer
                        doc.setTextColor(148, 163, 184);
                        doc.setFontSize(7);
                        doc.text(`Generated: ${new Date().toLocaleString()}  ·  Dolluz Corp EPR`, 105, 290, { align: "center" });

                        applyPdfWatermark(doc, await loadWatermarkDataUrl());
                        doc.save(`${e.code || "employee"}_${fullName.replace(/\s+/g, "_")}_profile.pdf`);
                        showToast(`${fullName}'s profile PDF downloaded`, "#10B981");
                      } catch (err) {
                        console.error("Profile PDF error:", err);
                        showToast("PDF generation failed — please try again", "error");
                      }
                    }}>📄 Export Profile PDF</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ── */}
      {confirmDel && (
        <div className="modal-overlay" style={{ left: 236 }}>
          <div className="modal" style={{ maxWidth: 400, width: "min(400px, calc(100% - 32px))" }}>
            <div style={{ padding: "28px" }}>
              <div style={{ fontSize: 28, textAlign: "center", marginBottom: 12 }}>⚠️</div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 16, color: "#0D1B2A", textAlign: "center", marginBottom: 8 }}>
                Deactivate Employee?
              </div>
              <div style={{ fontSize: 13, color: "#64748B", textAlign: "center", marginBottom: 24 }}>
                This will mark <strong>{empList.find(e => e.id === confirmDel).name}</strong> as <strong>Inactive</strong> and remove them from active rosters. Their record stays in the database — you can re-activate them by editing their employment status.
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button className="btn-secondary" style={{ flex: 1, justifyContent: "center" }} onClick={() => setConfirmDel(null)}>Cancel</button>
                <button className="btn-primary" style={{ flex: 1, justifyContent: "center", background: "#EF4444" }} onClick={() => deleteEmp(confirmDel)}>Yes, Deactivate</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showAdd || editEmp) && (
        <AddEmployeeModal
          onSave={saveEmp}
          onClose={() => { setShowAdd(false); setEditEmp(null); }}
          existing={editEmp || null}
          empList={empList}
        />
      )}

      {/* ── Import Confirm Modal ── */}
      {pendingImportFile && (
        <div className="modal-overlay" style={{ left: 236 }}>
          <div className="modal" style={{ maxWidth: 420, padding: "32px 30px", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📤</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 17, color: "#0D1B2A", marginBottom: 8 }}>
              Import Employees
            </div>
            <div style={{ fontSize: 12, color: "#64748B", marginBottom: 6 }}>Selected file:</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", background: "#F1F5F9", borderRadius: 8, padding: "8px 14px", marginBottom: 20, wordBreak: "break-all" }}>
              📎 {pendingImportFile.name}
            </div>
            <div style={{ fontSize: 12, color: "#64748B", marginBottom: 24, lineHeight: 1.6 }}>
              This will insert new employees and update existing ones based on <strong>Employee ID</strong>. Are you sure you want to proceed?
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn-secondary" style={{ flex: 1, justifyContent: "center", fontSize: 13 }}
                onClick={() => setPendingImportFile(null)}>
                Cancel
              </button>
              <button className="btn-primary" style={{ flex: 1, justifyContent: "center", fontSize: 13 }}
                onClick={() => handleImportXLS(pendingImportFile)}>
                ✓ Confirm Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Import Result Modal ── */}
      {importResult && (
        <div className="modal-overlay" style={{ left: 236 }}>
          <div className="modal" style={{ maxWidth: 600, width: "min(600px, calc(100% - 32px))" }}>
            <div style={{ padding: "28px" }}>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }}>
                <div style={{ fontSize: 32 }}>
                  {(importResult.parseErrors?.length || 0) + (importResult.insertErrors?.length || 0) === 0 ? "✅" : "⚠️"}
                </div>
                <div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 17, color: "#0D1B2A" }}>
                    Import Complete
                  </div>
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                    📎 {importResult.filename}
                  </div>
                </div>
              </div>

              {/* Summary pills */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 22 }}>
                {[
                  { label: "Total Rows", value: importResult.total, color: "#0D1B2A", bg: "#F8FAFC", border: "#E2E8F0" },
                  { label: "✓ Inserted", value: importResult.inserted, color: "#10B981", bg: "#F0FDF4", border: "#BBF7D0" },
                  { label: "↻ Updated", value: importResult.updated || 0, color: "#3B82F6", bg: "#EFF6FF", border: "#BFDBFE" },
                  {
                    label: "✗ Errors", value: (importResult.parseErrors?.length || 0) + (importResult.insertErrors?.length || 0),
                    color: "#EF4444", bg: "#FEF2F2", border: "#FECACA"
                  },
                ].map(s => (
                  <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: s.color, fontFamily: "'Sora',sans-serif" }}>{s.value}</div>
                    <div style={{ fontSize: 10, color: s.color, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Error details table */}
              {((importResult.parseErrors?.length || 0) + (importResult.insertErrors?.length || 0)) > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>
                    Error Details
                  </div>
                  <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #FEE2E2", borderRadius: 10, overflow: "hidden" }}>
                    {/* Table header */}
                    <div style={{ display: "grid", gridTemplateColumns: "90px 110px 1fr", gap: 0, background: "#FEF2F2", padding: "8px 14px", borderBottom: "1px solid #FEE2E2" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#991B1B", textTransform: "uppercase", letterSpacing: 0.4 }}>Row / Employee Id</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#991B1B", textTransform: "uppercase", letterSpacing: 0.4 }}>Name</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#991B1B", textTransform: "uppercase", letterSpacing: 0.4 }}>Error(s)</div>
                    </div>
                    {/* Parse errors */}
                    {(importResult.parseErrors || []).map((err, i) => (
                      <div key={`p${i}`} style={{ display: "grid", gridTemplateColumns: "90px 110px 1fr", gap: 0, padding: "9px 14px", borderBottom: "1px solid #FFF5F5", background: i % 2 === 0 ? "#fff" : "#FFFAFA", alignItems: "start" }}>
                        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, fontWeight: 600, color: "#EF4444" }}>
                          Row {err.row}<br />
                          <span style={{ fontWeight: 400, color: "#94A3B8", fontSize: 10 }}>{err.employee_id || err.employee_code || "—"}</span>
                        </div>
                        <div style={{ fontSize: 12, color: "#475569" }}>{err.name || "—"}</div>
                        <div>
                          {(Array.isArray(err.errors) ? err.errors : [err.errors || err.error]).map((msg, j) => (
                            <div key={j} style={{ fontSize: 12, color: "#64748B", marginBottom: j < (err.errors?.length || 1) - 1 ? 4 : 0 }}>
                              <span style={{ color: "#EF4444", fontWeight: 700, marginRight: 4 }}>•</span>{msg}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    {/* Insert/DB errors */}
                    {(importResult.insertErrors || []).map((err, i) => (
                      <div key={`i${i}`} style={{ display: "grid", gridTemplateColumns: "90px 110px 1fr", gap: 0, padding: "9px 14px", borderBottom: "1px solid #FFF5F5", background: i % 2 === 0 ? "#fff" : "#FFFAFA", alignItems: "start" }}>
                        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, fontWeight: 600, color: "#F59E0B" }}>
                          DB Error<br />
                          <span style={{ fontWeight: 400, color: "#94A3B8", fontSize: 10 }}>{err.code || "?"}</span>
                        </div>
                        <div style={{ fontSize: 12, color: "#475569" }}>{err.name || "—"}</div>
                        <div style={{ fontSize: 12, color: "#64748B" }}>
                          <span style={{ color: "#F59E0B", fontWeight: 700, marginRight: 4 }}>•</span>{err.error || err.message}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 8 }}>
                    Fix these rows in your Excel file and re-import. Successfully inserted/updated rows are already saved.
                  </div>
                </div>
              )}

              {/* Success message when no errors */}
              {(importResult.parseErrors?.length || 0) + (importResult.insertErrors?.length || 0) === 0 && (
                <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "14px 16px", marginBottom: 20, fontSize: 13, color: "#065F46", textAlign: "center" }}>
                  🎉 All {importResult.total} row{importResult.total !== 1 ? "s" : ""} imported successfully with no errors.
                </div>
              )}

              <button className="btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={() => setImportResult(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast} type={toastType} />}
    </div>
  );
};

export default EmployeeDatabase;